import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ReactMarkdown from 'react-markdown'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { PrimaryButton } from 'views/components/UI/Buttons'
import { MainLayout } from 'views/layouts'
import {
  Access,
  AssistantCitation,
  AssistantQueryResult,
  Audience,
  Audit,
  Content,
  Document,
  Item,
  ModuleConfiguration,
  Permission,
  Scope,
  Version,
  knowledgeApi,
} from 'services/knowledge'
import { authSelector } from 'ducks/auth'
const audienceNames: Record<Audience, string> = {
  CLIENT: 'Cliente',
  SUPPORT: 'Soluciones & Experiencia',
  TECHNOLOGY: 'Tecnología',
}
const states: Record<string, string> = {
  DRAFT: 'Borrador',
  IN_REVIEW: 'En revisión',
  PUBLISHED: 'Publicado',
  RETIRED: 'Retirado',
}
type KnowledgeTab = 'documents' | 'permissions' | 'connections' | 'agent' | 'architecture' | 'diagram' | 'assistant'
type ConnectionTab = 'operational' | 'storage' | 'database' | 'rag' | 'tas'
type AgentConfigTab = 'response' | 'sources' | 'conversation' | 'security'
type AssistantTraceEvent = {
  id: string
  at: string
  status: 'info' | 'pending' | 'success' | 'error'
  message: string
}
type AssistantChatMessage = {
  id: string
  at: string
  role: 'person' | 'assistant'
  status: 'pending' | 'complete' | 'error'
  text: string
  citations?: AssistantCitation[]
  companyContext?: AssistantQueryResult['company_context']
  timing?: {
    aiResponseMs: number
    applicationProcessingMs: number
    compatibilityProbeMs: number
    modelGenerationMs: number
    outputTokens: number
    tokensPerSecond: number
    gatewayPath: string
    thinkSent: boolean
    think: boolean
  }
}
type PendingUpload = {
  file: File
  documentId: number | null
  versionId: number | null
  revision: number | null
  phase: 'preparing' | 'uploading' | 'failed'
  progress: number
  message: string
}
const emptyContent = (): Content => ({
  title: '',
  summary: '',
  content: '',
  category: '',
  type_code: 'HOW_TO',
  audiences: ['CLIENT'],
  response_mode: 'FLEXIBLE',
  provenance: 'MANUAL',
  effective_from: null,
  review_after: null,
})
const contentOf = (v: Version): Content => ({
  title: v.title,
  summary: v.summary,
  content: v.content,
  category: v.category,
  type_code: v.type_code,
  audiences: v.audiences,
  response_mode: v.response_mode,
  provenance: v.provenance,
  effective_from: v.effective_from,
  review_after: v.review_after,
})
const scopeKey = (s: Scope) => `${s.scope}:${s.company_id || ''}`
const formatBytes = (size: number): string => {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KiB`
  return `${(size / (1024 * 1024)).toFixed(2)} MiB`
}
const formatAssistantDuration = (milliseconds: number): string => {
  if (milliseconds < 1000) return `${milliseconds} ms`
  return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)} s`
}
const resourceTypeLabel = (mime: string): string =>
  ({
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'image/png': 'PNG',
    'image/jpeg': 'JPEG',
    'text/plain': 'TXT',
    'text/markdown': 'Markdown',
  }[mime] || mime)
const errorText = (error: unknown): string => {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response
  const payload = response?.data
  const payloadCode = typeof payload === 'string' ? payload : ''
  const code = [payloadCode, payload?.message, payload?.error, payload?.code, (error as Error)?.message]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
  if (code.includes('Authentication Error') || code.includes('Authentication session'))
    return 'La sesión ha caducado o no pudo renovarse. Inicia sesión de nuevo para continuar con la IA local.'
  if (code.includes('REVISION_CONFLICT'))
    return 'El documento cambió. Recarga antes de continuar; conserva tu texto para compararlo.'
  if (code.includes('PRIVATE_STORAGE_AWS_LOGIN_REQUIRED'))
    return 'La sesión de AWS ha caducado o no está disponible. Renueva la sesión AWS del equipo y vuelve a comprobarlo.'
  if (code.includes('PRIVATE_STORAGE_ACCESS_DENIED'))
    return 'Las credenciales de AWS no tienen permiso para validar el bucket privado configurado.'
  if (code.includes('PRIVATE_STORAGE_BUCKET_NOT_FOUND'))
    return 'El bucket privado configurado no existe o no está disponible en la región de AWS seleccionada.'
  if (code.includes('PRIVATE_STORAGE_UNREACHABLE'))
    return 'No se pudo conectar con AWS. Comprueba la red, VPN o proxy del equipo y vuelve a intentarlo.'
  if (code.includes('PRIVATE_STORAGE'))
    return 'El almacenamiento privado requiere validación antes de habilitar archivos.'
  if (code.includes('RAG_SESSION_TOKEN_NOT_ALLOWED'))
    return 'Usa una API Key de la IA local, no un token de sesión.'
  if (code.includes('RAG_GATEWAY_URL_INVALID'))
    return 'La dirección de la IA local debe usar HTTP o HTTPS, sin rutas ni credenciales incrustadas.'
  if (code.includes('RAG_MODELS_CONFIGURATION_INCOMPLETE'))
    return 'Introduce la dirección de la IA local y la API Key para leer los modelos.'
  if (code.includes('RAG_MODELS_AUTH_FAILED'))
    return 'La IA local rechazó la API Key.'
  if (code.includes('RAG_MODELS_INVALID_RESPONSE'))
    return 'La IA local respondió sin modelos disponibles para este usuario.'
  if (code.includes('RAG_MODELS_UNAVAILABLE'))
    return 'No se pudo consultar la IA local. Verifica la dirección, el puerto y la conectividad.'
  if (code.includes('RAG_ENABLED_CONFIGURATION_INCOMPLETE'))
    return 'Para activar la IA completa la URL segura, el modelo y la API Key.'
  if (code.includes('KNOWLEDGE_ASSISTANT_DISABLED'))
    return 'La sincronización de conocimiento con la IA local todavía no está activada.'
  if (code.includes('KNOWLEDGE_ASSISTANT_ACCESS_REQUIRED'))
    return 'No tienes permiso para consultar conocimiento con la IA local.'
  if (code.includes('KNOWLEDGE_ASSISTANT_PREVIEW_EMPTY'))
    return 'El corpus temporal no tiene contenido todavía. Sube un archivo o guarda texto en un borrador autorizado antes de consultar la IA.'
  if (code.includes('KNOWLEDGE_ASSISTANT_PUBLISHED_EMPTY'))
    return 'No hay documentación publicada y aprobada para IA disponible para esta consulta. Activa el corpus temporal de pruebas o publica y aprueba un documento.'
  if (response?.status === 404)
    return 'La API de pruebas de IA no está desplegada todavía en el servidor seleccionado.'
  if (code.includes('RAG_GATEWAY_ECONNREFUSED') || code.includes('RAG_GATEWAY_NETWORK_ERROR'))
    return 'La IA local no está disponible: el gateway rechazó la conexión. Inicia el servicio del modelo o revisa la URL y el puerto configurados en Conexiones.'
  if (code.includes('RAG_GATEWAY'))
    return 'La IA local no pudo atender la consulta. Verifica su conexión y vuelve a intentarlo.'
  if (code.includes('UNPUBLISHED_DRAFT_REQUIRED'))
    return 'Solo pueden eliminarse borradores que nunca se hayan publicado.'
  if (code.includes('TAS_API_BASE_URL'))
    return 'La dirección de la API TAS debe ser una URL HTTPS válida.'
  if (code.includes('TAS_API_KEY_REQUIRED') || code.includes('TAS_HTTP_401') || code.includes('TAS_HTTP_403'))
    return 'El TAS rechazó la API Key.'
  if (code.includes('TAS_NETWORK') || code.includes('TAS_ECONN') || code.includes('TAS_ETIMEDOUT'))
    return 'No se pudo alcanzar el TAS. Verifica la dirección, el puerto y la conectividad.'
  if (code.includes('KNOWLEDGE_CONFIGURATION_KEY_REQUIRED'))
    return 'Falta la clave maestra de cifrado en la configuración segura del servidor.'
  if (code.includes('KNOWLEDGE_CONFIGURATION_TABLE_REQUIRED'))
    return 'Falta aplicar la preparación de base de datos de este módulo.'
  if (code.includes('KNOWLEDGE_COMPANY_DATA_CONNECTION_INCOMPLETE'))
    return 'Configura las credenciales de solo lectura para la fuente Base de datos antes de habilitarla para la IA.'
  if (code.includes('404') || code.includes('NOT_FOUND')) return 'Contenido inexistente o sin acceso.'
  return 'No se completó la operación. Verifica tus permisos, los campos y el estado editorial.'
}
const yesNo = (value: boolean): string => (value ? 'Sí' : 'No')
const configuredValue = (value: string | number | null): string =>
  value === null || value === '' ? 'Pendiente de configurar' : String(value)
const defaultInitialGreeting =
  'Hola. Estoy aqui para ayudarte con procesos y dudas de Bluesite HR Hub. Dime que necesitas hacer y lo revisamos juntos.'

const localPreviewConfiguration = (): ModuleConfiguration => ({
  source: 'LOCAL_PREVIEW',
  database: {
    configured: true,
    accessible: true,
    scope: 'SESSION_COMPANY',
  },
  resources: {
    private_storage_enabled: true,
    bucket_configured: true,
    max_size_mib: 10,
    allowed_types: [
      { mime: 'application/pdf', label: 'PDF' },
      { mime: 'image/png', label: 'PNG' },
      { mime: 'image/jpeg', label: 'JPEG' },
      {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        label: 'DOCX',
      },
      { mime: 'text/plain', label: 'TXT' },
      { mime: 'text/markdown', label: 'Markdown' },
    ],
  },
  rag: {
    enabled: false,
    gateway: null,
    api_key_configured: false,
    model_id: null,
    timeout_ms: 10000,
    batch_size: 10,
    poll_ms: 5000,
    max_attempts: 8,
    response_profile: {
      audience: 'Usuario final de Bluesite',
      style: 'PRACTICAL',
      instructions: '',
    },
    agent_policy: {
      role: 'SUPPORT_SPECIALIST',
      response_format: 'STEP_BY_STEP',
      detail_level: 'STANDARD',
      source_priority: 'PUBLISHED_FIRST',
      min_confidence: 60,
      clarification_mode: 'ASK',
      citations_required: true,
      require_evidence: true,
      rules: '',
      internet_enabled: false,
      company_data_enabled: false,
      streaming_enabled: true,
      max_tokens: 600,
    },
    conversation_policy: {
      courtesy_enabled: true,
      greeting_type: 'ANY',
      farewell_type: 'ANY',
      greeting_formality: 50,
      farewell_formality: 50,
      greeting_intent: defaultInitialGreeting,
      greeting_response: defaultInitialGreeting,
      thanks_response: 'De nada. Cuando quieras, puedo ayudarte a revisar otro proceso o incidencia de Bluesite HR Hub.',
      farewell_response: 'Hasta luego. Aqui estare cuando necesites ayuda con Bluesite HR Hub.',
      safety_response: 'No puedo ayudar con lenguaje ofensivo ni con solicitudes que puedan causar dano. Reformula la consulta de forma respetuosa y centrada en una tarea de Bluesite HR Hub.',
      unrecognized_response: 'No he entendido del todo la consulta. ¿Puedes reformularla o indicar qué proceso, incidencia o acción de Bluesite HR Hub necesitas revisar?',
      blocked_terms: '',
    },
    mtls_required: false,
    mtls_configured: false,
  },
  tas: {
    enabled: false,
    endpoint: null,
    api_key_configured: false,
    timeout_ms: 10000,
    pedido_id: null,
    created_by_employee_id: null,
    responsible_employee_id: null,
    initial_state: 'Pendiente',
  },
})

const isUnavailableConfigurationEndpoint = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404

type RagConfigurationForm = {
  enabled: boolean
  gateway: string
  modelId: string
  apiKey: string
  timeoutMs: number
  batchSize: number
  pollMs: number
  maxAttempts: number
  responseAudience: string
  responseStyle: 'PRACTICAL' | 'BRIEF' | 'TECHNICAL' | 'SUPPORT'
  responseInstructions: string
}

type AgentPolicyForm = {
  audience: string
  style: 'PRACTICAL' | 'BRIEF' | 'TECHNICAL' | 'SUPPORT'
  instructions: string
  role: 'SUPPORT_SPECIALIST' | 'HR_ADVISOR' | 'PLATFORM_ADMIN' | 'END_USER_GUIDE'
  responseFormat: 'STEP_BY_STEP' | 'INCIDENT_DIAGNOSIS' | 'CHECKLIST' | 'EXPLANATION'
  detailLevel: 'BRIEF' | 'STANDARD' | 'DETAILED'
  sourcePriority: 'PUBLISHED_FIRST' | 'MOST_RECENT' | 'COMPANY_FIRST' | 'TEST_CORPUS'
  minConfidence: number
  clarificationMode: 'ASK' | 'LIMITED_ANSWER'
  citationsRequired: boolean
  requireEvidence: boolean
  rules: string
  internetEnabled: boolean
  companyDataEnabled: boolean
  streamingEnabled: boolean
  courtesyEnabled: boolean
  greetingFormality: number
  farewellFormality: number
  greetingIntent: string
  greetingResponse: string
  thanksResponse: string
  farewellResponse: string
  safetyResponse: string
  unrecognizedResponse: string
  blockedTerms: string
  maxTokens: number
}

type TasConfigurationForm = {
  enabled: boolean
  endpoint: string
  apiKey: string
  timeoutMs: number
  pedidoId: number | null
  createdByEmployeeId: number | null
  responsibleEmployeeId: number | null
}

type OperationalCheckId = 'storage' | 'database' | 'rag' | 'tas'
type OperationalCheckState = 'pending' | 'running' | 'success' | 'error'
type OperationalCheck = {
  id: OperationalCheckId
  label: string
  state: OperationalCheckState
  detail: string
}

const operationalChecksInitial = (): OperationalCheck[] => [
  { id: 'storage', label: 'Almacenamiento AWS', state: 'pending', detail: 'Pendiente de comprobar.' },
  { id: 'database', label: 'Base de datos', state: 'pending', detail: 'Pendiente de comprobar.' },
  { id: 'rag', label: 'IA local', state: 'pending', detail: 'Pendiente de comprobar.' },
  { id: 'tas', label: 'Conexión TAS', state: 'pending', detail: 'Pendiente de comprobar.' },
]

const ragFormFrom = (configuration: ModuleConfiguration): RagConfigurationForm => ({
  enabled: configuration.rag.enabled,
  gateway: configuration.rag.gateway || '',
  modelId: configuration.rag.model_id || '',
  apiKey: '',
  timeoutMs: configuration.rag.timeout_ms,
  batchSize: configuration.rag.batch_size,
  pollMs: configuration.rag.poll_ms,
  maxAttempts: configuration.rag.max_attempts,
  responseAudience: configuration.rag.response_profile.audience,
  responseStyle: configuration.rag.response_profile.style,
  responseInstructions: configuration.rag.response_profile.instructions,
})

const agentPolicyFormFrom = (configuration: ModuleConfiguration): AgentPolicyForm => ({
  audience: configuration.rag.response_profile.audience,
  style: configuration.rag.response_profile.style,
  instructions: configuration.rag.response_profile.instructions,
  role: configuration.rag.agent_policy.role,
  responseFormat: configuration.rag.agent_policy.response_format,
  detailLevel: configuration.rag.agent_policy.detail_level,
  sourcePriority: configuration.rag.agent_policy.source_priority,
  minConfidence: configuration.rag.agent_policy.min_confidence,
  clarificationMode: configuration.rag.agent_policy.clarification_mode,
  citationsRequired: configuration.rag.agent_policy.citations_required,
  requireEvidence: configuration.rag.agent_policy.require_evidence,
  rules: configuration.rag.agent_policy.rules,
  internetEnabled: configuration.rag.agent_policy.internet_enabled,
  companyDataEnabled: configuration.rag.agent_policy.company_data_enabled,
  streamingEnabled: configuration.rag.agent_policy.streaming_enabled,
  maxTokens: configuration.rag.agent_policy.max_tokens,
  courtesyEnabled: configuration.rag.conversation_policy.courtesy_enabled,
  greetingFormality: configuration.rag.conversation_policy.greeting_formality,
  farewellFormality: configuration.rag.conversation_policy.farewell_formality,
  greetingIntent: configuration.rag.conversation_policy.greeting_intent,
  greetingResponse: configuration.rag.conversation_policy.greeting_response,
  thanksResponse: configuration.rag.conversation_policy.thanks_response,
  farewellResponse: configuration.rag.conversation_policy.farewell_response,
  safetyResponse: configuration.rag.conversation_policy.safety_response,
  unrecognizedResponse: configuration.rag.conversation_policy.unrecognized_response,
  blockedTerms: configuration.rag.conversation_policy.blocked_terms,
})

const assistantInitialGreeting = (configuration: ModuleConfiguration | null): string =>
  configuration?.rag.conversation_policy.greeting_intent?.trim() || defaultInitialGreeting

const tasFormFrom = (configuration: ModuleConfiguration): TasConfigurationForm => ({
  enabled: configuration.tas.enabled,
  endpoint: configuration.tas.endpoint || '',
  apiKey: '',
  timeoutMs: configuration.tas.timeout_ms,
  pedidoId: configuration.tas.pedido_id,
  createdByEmployeeId: configuration.tas.created_by_employee_id,
  responsibleEmployeeId: configuration.tas.responsible_employee_id,
})

function ConfigurationValue({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(190px, 0.8fr) 1.2fr' },
        gap: { xs: 0.25, sm: 2 },
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  )
}

function ConfigurationSection({
  title,
  status,
  children,
  defaultExpanded = false,
}: {
  title: string
  status: JSX.Element
  children: ReactNode
  defaultExpanded?: boolean
}): JSX.Element {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 60 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" pr={1} spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {status}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>{children}</AccordionDetails>
    </Accordion>
  )
}

function ArchitectureNode({
  icon,
  title,
  detail,
  status,
  pending = false,
}: {
  icon: ReactNode
  title: string
  detail: string
  status: string
  pending?: boolean
}): JSX.Element {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: pending ? 'warning.light' : 'divider',
        borderRadius: 1,
        p: 1.5,
        minHeight: 152,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1} height="100%">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 34,
              height: 34,
              borderRadius: 1,
              color: pending ? 'warning.dark' : 'primary.main',
              bgcolor: 'action.hover',
            }}
          >
            {icon}
          </Box>
          <Chip
            size="small"
            color={pending ? 'warning' : 'success'}
            variant={pending ? 'outlined' : 'filled'}
            label={status}
          />
        </Stack>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </Stack>
    </Box>
  )
}

function ArchitectureArrow({ label }: { label: string }): JSX.Element {
  return (
    <Stack alignItems="center" spacing={0.25} color="primary.main">
      <ArrowForwardOutlinedIcon />
      <Typography variant="caption" color="text.secondary" align="center">
        {label}
      </Typography>
    </Stack>
  )
}

export function KnowledgePanel({ api = knowledgeApi }: { api?: typeof knowledgeApi }): JSX.Element {
  const { role } = useSelector(authSelector)
  const [access, setAccess] = useState<Access | null>(null),
    [items, setItems] = useState<Item[]>([])
  const [doc, setDoc] = useState<Document | null>(null),
    [versionId, setVersionId] = useState<number | null>(null)
  const [form, setForm] = useState<Content>(emptyContent),
    [creating, setCreating] = useState(false),
    [dirty, setDirty] = useState(false)
  const [selectedScope, setScope] = useState<Scope>({ scope: 'GLOBAL', company_id: null })
  const directUploadInputRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const [filter, setFilter] = useState<Record<string, string>>({}),
    [page, setPage] = useState(1),
    [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [reason, setReason] = useState('')
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null)
  const [audits, setAudits] = useState<Audit[]>([]),
    [auditPage, setAuditPage] = useState(1),
    [showAudit, setShowAudit] = useState(false)
  const [auditAnchor, setAuditAnchor] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [activeTab, setActiveTab] = useState<KnowledgeTab>('documents')
  const [connectionTab, setConnectionTab] = useState<ConnectionTab>('operational')
  const [agentConfigTab, setAgentConfigTab] = useState<AgentConfigTab>('response')
  const [configuration, setConfiguration] = useState<ModuleConfiguration | null>(null)
  const [ragForm, setRagForm] = useState<RagConfigurationForm>(() =>
    ragFormFrom(localPreviewConfiguration())
  )
  const [agentPolicyForm, setAgentPolicyForm] = useState<AgentPolicyForm>(() =>
    agentPolicyFormFrom(localPreviewConfiguration())
  )
  const [tasForm, setTasForm] = useState<TasConfigurationForm>(() =>
    tasFormFrom(localPreviewConfiguration())
  )
  const [ragModels, setRagModels] = useState<Array<{ id: string; name: string }>>([])
  const [ragConnectionModels, setRagConnectionModels] = useState<number | null>(null)
  const [ragConnectionError, setRagConnectionError] = useState('')
  const [showRagApiKey, setShowRagApiKey] = useState(false)
  const [showTasApiKey, setShowTasApiKey] = useState(false)
  const [tasConnectionResult, setTasConnectionResult] = useState('')
  const [tasConnectionError, setTasConnectionError] = useState('')
  const [storageConnectionResult, setStorageConnectionResult] = useState('')
  const [storageConnectionError, setStorageConnectionError] = useState('')
  const [databaseConnectionResult, setDatabaseConnectionResult] = useState('')
  const [databaseConnectionError, setDatabaseConnectionError] = useState('')
  const [operationalChecks, setOperationalChecks] = useState<OperationalCheck[] | null>(null)
  const [operationalChecking, setOperationalChecking] = useState(false)
  const [assistantQuestion, setAssistantQuestion] = useState('')
  const [assistantLoading, setAssistantLoading] = useState(false)
  const [assistantTrace, setAssistantTrace] = useState<AssistantTraceEvent[]>([])
  const [assistantConversation, setAssistantConversation] = useState<AssistantChatMessage[]>([])
  const [assistantPreproduction, setAssistantPreproduction] = useState(false)
  const [assistantThinking, setAssistantThinking] = useState(false)
  const [grant, setGrant] = useState<Omit<Permission, 'id'>>({
    user_id: 0,
    scope: 'GLOBAL',
    company_id: null,
    can_edit: false,
    can_publish: false,
    readable_audiences: ['CLIENT'],
    active: true,
  })
  const v = doc?.versions.find((x) => x.id === versionId)
  const writable = creating || (v?.state === 'DRAFT' && doc?.capabilities.edit)
  const currentCaps = access?.scopes.find(
    (s) => scopeKey(s) === scopeKey(creating ? selectedScope : doc || selectedScope)
  )
  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await work()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const readRagModels = async () => {
    const gateway = ragForm.gateway.trim().replace(/\\/g, '/')
    if (!gateway) {
      const message = 'Introduce la dirección de la IA local para comprobar la conexión y cargar los modelos.'
      setRagConnectionModels(null)
      setRagConnectionError(message)
      setError(message)
      return
    }
    if (gateway !== ragForm.gateway)
      setRagForm((previous) => ({ ...previous, gateway }))
    setRagConnectionModels(null)
    setRagConnectionError('')
    setBusy(true)
    setError('')
    try {
      const models = await api.ragModels({
        rag_gateway_url: gateway,
        ...(ragForm.apiKey.trim() ? { rag_api_key: ragForm.apiKey } : {}),
      })
      setRagModels(models)
      setRagConnectionModels(models.length)
      if (!models.some((model) => model.id === ragForm.modelId))
        setRagForm((previous) => ({ ...previous, modelId: models[0]?.id || '' }))
      setNotice(`${models.length} modelos disponibles en la IA local.`)
    } catch (requestError) {
      const message = errorText(requestError)
      setError(message)
      setRagConnectionError(message)
    } finally {
      setBusy(false)
    }
  }
  const updateOperationalCheck = (
    id: OperationalCheckId,
    state: OperationalCheckState,
    detail: string,
  ) => {
    setOperationalChecks((checks) =>
      (checks || operationalChecksInitial()).map((check) =>
        check.id === id ? { ...check, state, detail } : check,
      ),
    )
  }
  const runOperationalChecks = async () => {
    setBusy(true)
    setOperationalChecking(true)
    setError('')
    setNotice('')
    setOperationalChecks(operationalChecksInitial())

    let failures = 0
    try {
      updateOperationalCheck('storage', 'running', 'Comprobando protecciones del bucket privado...')
      try {
        const result = await api.storageConnection()
        const detail = `Correcto. Bucket privado ${result.bucket} protegido.`
        setStorageConnectionError('')
        setStorageConnectionResult(`Almacenamiento AWS correcto. ${detail}`)
        updateOperationalCheck('storage', 'success', detail)
      } catch (requestError) {
        failures += 1
        const detail = errorText(requestError)
        setStorageConnectionResult('')
        setStorageConnectionError(detail)
        updateOperationalCheck('storage', 'error', detail)
      }

      updateOperationalCheck('database', 'running', 'Comprobando conexión y aislamiento por empresa...')
      try {
        const result = await api.databaseConnection()
        const detail = `Correcta. Base de datos ${result.source} accesible con alcance de empresa activa.`
        setDatabaseConnectionError('')
        setDatabaseConnectionResult(detail)
        updateOperationalCheck('database', 'success', detail)
      } catch (requestError) {
        failures += 1
        const detail = errorText(requestError)
        setDatabaseConnectionResult('')
        setDatabaseConnectionError(detail)
        updateOperationalCheck('database', 'error', detail)
      }

      updateOperationalCheck('rag', 'running', 'Consultando modelos disponibles en la IA local...')
      try {
        const gateway = ragForm.gateway.trim().replace(/\\/g, '/')
        if (!gateway) throw new Error('RAG_MODELS_CONFIGURATION_INCOMPLETE')
        const models = await api.ragModels({
          rag_gateway_url: gateway,
          ...(ragForm.apiKey.trim() ? { rag_api_key: ragForm.apiKey } : {}),
        })
        setRagModels(models)
        setRagConnectionModels(models.length)
        setRagConnectionError('')
        if (!models.some((model) => model.id === ragForm.modelId))
          setRagForm((previous) => ({ ...previous, modelId: models[0]?.id || '' }))
        updateOperationalCheck('rag', 'success', `Correcta. ${models.length} modelos disponibles.`)
      } catch (requestError) {
        failures += 1
        const detail = errorText(requestError)
        setRagConnectionModels(null)
        setRagConnectionError(detail)
        updateOperationalCheck('rag', 'error', detail)
      }

      updateOperationalCheck('tas', 'running', 'Comprobando acceso al pedido de destino en TAS...')
      try {
        const endpoint = tasForm.endpoint.trim()
        if (!endpoint || !tasForm.pedidoId) throw new Error('TAS_API_BASE_URL')
        const result = await api.tasConnection({
          tas_api_base_url: endpoint,
          ...(tasForm.apiKey.trim() ? { tas_api_key: tasForm.apiKey } : {}),
          tas_timeout_ms: tasForm.timeoutMs,
          tas_pedido_id: tasForm.pedidoId,
        })
        const detail = `Correcta. Acceso verificado al pedido ${result.pedido_id}.`
        setTasConnectionError('')
        setTasConnectionResult(`Conexión con el TAS correcta. ${detail}`)
        updateOperationalCheck('tas', 'success', detail)
      } catch (requestError) {
        failures += 1
        const detail = errorText(requestError)
        setTasConnectionResult('')
        setTasConnectionError(detail)
        updateOperationalCheck('tas', 'error', detail)
      }

      setNotice(
        failures
          ? `Comprobación terminada: ${failures} servicio${failures === 1 ? '' : 's'} requiere revisión.`
          : 'Comprobación terminada. Todos los servicios operativos están disponibles.',
      )
    } finally {
      setOperationalChecking(false)
      setBusy(false)
    }
  }
  async function list(nextPage = page) {
    const data = await api.list({
      ...Object.fromEntries(Object.entries(filter).filter(([, value]) => value)),
      page: nextPage,
    })
    setItems(data.items)
    setMore(data.has_more)
  }
  async function load(id: number, preferId?: number) {
    const next = await api.get(id)
    setDoc(next)
    const selected = next.versions.find((x) => x.id === preferId) || next.versions[0]
    setVersionId(selected.id)
    setForm(contentOf(selected))
    setScope({ scope: next.scope, company_id: next.company_id })
    setCreating(false)
    setDirty(false)
    setReason('')
    setShowAudit(false)
  }
  useEffect(() => {
    let live = true
    api
      .access()
      .then((a) => {
        if (live) setAccess(a)
      })
      .catch((e) => {
        if (live) setError(errorText(e))
      })
    return () => {
      live = false
    }
  }, [api])
  useEffect(() => {
    let live = true
    api
      .list({ ...Object.fromEntries(Object.entries(filter).filter(([, value]) => value)), page })
      .then((data) => {
        if (live) {
          setItems(data.items)
          setMore(data.has_more)
        }
      })
      .catch((e) => {
        if (live) setError(errorText(e))
      })
    return () => {
      live = false
    }
  }, [api, filter, page])
  useEffect(() => {
    if (activeTab !== 'documents' || (!creating && !doc?.id)) return

    const frame = window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [
    creating,
    doc?.id,
    activeTab,
  ])
  const change = <K extends keyof Content>(key: K, value: Content[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }))
    setDirty(true)
  }
  async function save() {
    if (!form.title.trim() || !form.audiences.length) {
      setError('Completa título y audiencia.')
      return
    }
    await run(async () => {
      if (creating) {
        const created = await api.create({ ...form, ...selectedScope })
        await load(created.document_id)
      } else if (doc && v) {
        await api.save(doc.id, v.id, doc.revision, form)
        await load(doc.id, v.id)
      }
      await list()
      setNotice('Borrador guardado.')
    })
  }
  const attachFile = async (
    file: File,
    document: Document,
    draft: Version,
  ) => {
    setPendingUpload({
      file,
      documentId: document.id,
      versionId: draft.id,
      revision: document.revision,
      phase: 'uploading',
      progress: 5,
      message: 'Enviando el archivo al almacenamiento privado...',
    })
    await api.attach(document.id, draft.id, document.revision, file, (loaded, total) => {
      const progress = total ? Math.min(99, Math.round((loaded / total) * 100)) : 60
      setPendingUpload((current) =>
        current?.file === file
          ? { ...current, progress, message: 'Guardando y convirtiendo el archivo a Markdown...' }
          : current,
      )
    })
    await load(document.id, draft.id)
    await list(1)
    setPendingUpload(null)
    setNotice(`Archivo ${file.name} adjuntado y preparado para su conversión a Markdown.`)
  }
  const uploadNewDocument = (file: File) => {
    if (file.size > 10485760) {
      setError('El archivo supera 10 MiB.')
      return
    }
    const editableScope = access?.scopes.find((candidate) => candidate.edit)
    if (!editableScope) {
      setError('No tienes permisos para crear documentos en este alcance.')
      return
    }
    const filenameWithoutExtension = file.name.replace(/\.[^.]+$/, '').trim()
    setError('')
    setNotice('')
    setPendingUpload({
      file,
      documentId: null,
      versionId: null,
      revision: null,
      phase: 'preparing',
      progress: 0,
      message: 'Creando el borrador del documento...',
    })
    void (async () => {
      const created = await api.create({
        ...emptyContent(),
        title: filenameWithoutExtension || 'Documento adjunto',
        summary: `Documento importado: ${file.name}`,
        scope: editableScope.scope,
        company_id: editableScope.company_id,
      })
      const createdDocument = await api.get(created.document_id)
      const draft = createdDocument.versions.find((version) => version.state === 'DRAFT')
      if (!draft) throw new Error('No se pudo preparar el borrador para el archivo seleccionado.')

      // Keep the draft open even if the attachment service reports an error.
      setDoc(createdDocument)
      setVersionId(draft.id)
      setScope({ scope: editableScope.scope, company_id: editableScope.company_id })
      setCreating(false)
      setDirty(false)
      await attachFile(file, createdDocument, draft)
    })().catch((uploadError) => {
      setPendingUpload((current) =>
        current
          ? { ...current, phase: 'failed', message: errorText(uploadError) }
          : current,
      )
      setError(`No se pudo adjuntar el archivo. ${errorText(uploadError)}`)
    })
  }
  const retryPendingUpload = () => {
    if (!pendingUpload || pendingUpload.phase !== 'failed') return
    const { file, documentId } = pendingUpload
    if (!documentId) {
      uploadNewDocument(file)
      return
    }
    setError('')
    void (async () => {
      const current = await api.get(documentId)
      const draft = current.versions.find((version) => version.id === pendingUpload.versionId)
      if (!draft) throw new Error('No se encontró el borrador pendiente para reintentar la carga.')
      const alreadyAttached = draft.resources.some(
        (resource) => resource.filename === file.name && resource.size === file.size,
      )
      if (alreadyAttached) {
        await load(current.id, draft.id)
        await list(1)
        setPendingUpload(null)
        setNotice(`El archivo ${file.name} ya estaba adjuntado al borrador.`)
        return
      }
      setDoc(current)
      setVersionId(draft.id)
      await attachFile(file, current, draft)
    })().catch((uploadError) => {
      setPendingUpload((current) =>
        current ? { ...current, phase: 'failed', message: errorText(uploadError) } : current,
      )
      setError(`No se pudo reintentar la carga. ${errorText(uploadError)}`)
    })
  }
  const action = (operation: string) =>
    run(async () => {
      if (!doc || !v) return
      await api.action(doc.id, v.id, doc.revision, operation, operation === 'reject' ? reason : undefined)
      await load(doc.id, operation === 'copy' ? undefined : v.id)
      await list()
      setNotice('Operación editorial registrada.')
    })
  const scopeLabel = (s: Scope) =>
    s.scope === 'GLOBAL'
      ? 'Institucional (GLOBAL)'
      : `${access?.companies.find((c) => c.id === s.company_id)?.name || 'Empresa'} (#${s.company_id})`
  const scopeSelect = (value: Scope, onChange: (s: Scope) => void, onlyEdit = false) => (
    <TextField
      select
      size="small"
      label="Alcance y empresa"
      value={scopeKey(value)}
      onChange={(e) => {
        const found = access?.scopes.find((s) => scopeKey(s) === e.target.value)
        if (found) onChange({ scope: found.scope, company_id: found.company_id })
      }}
      disabled={busy}
      fullWidth
    >
      {access?.scopes
        .filter((s) => !onlyEdit || s.edit)
        .map((s) => (
          <MenuItem key={scopeKey(s)} value={scopeKey(s)}>
            {scopeLabel(s)}
          </MenuItem>
        ))}
    </TextField>
  )
  if (!access)
    return (
      <>
        {error ? <Box p={3}><Alert severity="error">{error}</Alert></Box> : <ModalLoader isLoading />}
      </>
    )
  const canManageConfiguration =
    access.manage_configuration ||
    ['super_admin', 'special_admin', 'platform_admin', 'super_platform_admin'].includes(role)
  const showWorkspace = activeTab === 'documents'
  const architectureConfiguration = configuration || localPreviewConfiguration()
  const privateStorageReady =
    architectureConfiguration.resources.private_storage_enabled &&
    architectureConfiguration.resources.bucket_configured
  const ragConfigured =
    Boolean(architectureConfiguration.rag.gateway) &&
    Boolean(architectureConfiguration.rag.model_id) &&
    architectureConfiguration.rag.api_key_configured
  const ragReady = architectureConfiguration.rag.enabled && ragConfigured
  const tasConfigured =
    Boolean(architectureConfiguration.tas.endpoint) &&
    architectureConfiguration.tas.api_key_configured &&
    Boolean(architectureConfiguration.tas.pedido_id) &&
    Boolean(architectureConfiguration.tas.created_by_employee_id) &&
    Boolean(architectureConfiguration.tas.responsible_employee_id)
  const tasReady = architectureConfiguration.tas.enabled && tasConfigured
  const hasOperationalFailure = operationalChecks?.some((check) => check.state === 'error') || false
  const allOperationalChecksPassed =
    operationalChecks?.length === operationalChecksInitial().length &&
    operationalChecks.every((check) => check.state === 'success')
  const operationalStatus =
    configuration?.source === 'LOCAL_PREVIEW'
      ? { color: 'warning' as const, label: 'Vista local temporal' }
      : hasOperationalFailure
        ? { color: 'error' as const, label: 'Requiere revisión' }
        : allOperationalChecksPassed
          ? { color: 'success' as const, label: 'Operativa' }
          : { color: 'default' as const, label: 'Sin comprobar' }
  const selectTab = (nextTab: KnowledgeTab) => {
    if (nextTab === activeTab) return
    if (nextTab === 'permissions') {
      run(async () => {
        setPermissions(await api.permissions())
        setActiveTab('permissions')
      })
      return
    }
    if (nextTab === 'connections' || nextTab === 'agent') {
      run(async () => {
        try {
          const next = await api.configuration()
          setConfiguration(next)
          setRagForm(ragFormFrom(next))
          setAgentPolicyForm(agentPolicyFormFrom(next))
          setTasForm(tasFormFrom(next))
        } catch (requestError) {
          if (window.location.hostname !== 'localhost' || !isUnavailableConfigurationEndpoint(requestError))
            throw requestError
          const next = localPreviewConfiguration()
          setConfiguration(next)
          setRagForm(ragFormFrom(next))
          setAgentPolicyForm(agentPolicyFormFrom(next))
          setTasForm(tasFormFrom(next))
        }
        setRagModels([])
        setRagConnectionModels(null)
        setRagConnectionError('')
        setActiveTab(nextTab)
      })
      return
    }
    if (nextTab === 'assistant') {
      run(async () => {
        try {
          setConfiguration(await api.configuration())
        } catch (requestError) {
          if (window.location.hostname !== 'localhost' || !isUnavailableConfigurationEndpoint(requestError))
            throw requestError
          setConfiguration(localPreviewConfiguration())
        }
        setActiveTab('assistant')
      })
      return
    }
    if (nextTab === 'architecture') {
      run(async () => {
        try {
          setConfiguration(await api.configuration())
        } catch (requestError) {
          if (window.location.hostname !== 'localhost' || !isUnavailableConfigurationEndpoint(requestError))
            throw requestError
          setConfiguration(localPreviewConfiguration())
        }
        setActiveTab('architecture')
      })
      return
    }
    setActiveTab(nextTab)
  }
  const addAssistantTrace = (status: AssistantTraceEvent['status'], message: string) => {
    setAssistantTrace((previous) => [
      ...previous,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        at: new Intl.DateTimeFormat('es-CO', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }).format(new Date()),
        status,
        message,
      },
    ])
  }
  const initializeAssistantConversation = () => {
    const at = new Intl.DateTimeFormat('es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date())
    setAssistantQuestion('')
    setAssistantTrace([])
    setAssistantConversation([{
      id: `${Date.now()}-initial-greeting`,
      at,
      role: 'assistant',
      status: 'complete',
      text: assistantInitialGreeting(configuration),
    }])
  }
  const consultAssistant = () => {
    const query = assistantQuestion.trim()
    const streamingEnabled = configuration?.rag.agent_policy.streaming_enabled !== false
    const companyDataEnabled = configuration?.rag.agent_policy.company_data_enabled === true
    const at = new Intl.DateTimeFormat('es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date())
    const responseId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    setAssistantConversation((previous) => [
      ...(previous.length > 0
        ? previous
        : [{
            id: `${responseId}-initial-greeting`,
            at,
            role: 'assistant' as const,
            status: 'complete' as const,
            text: assistantInitialGreeting(configuration),
          }]),
      { id: `${responseId}-person`, at, role: 'person', status: 'complete', text: query },
      {
        id: responseId,
        at,
        role: 'assistant',
        status: 'pending',
        text: 'Buscando la información autorizada para responderte...',
      },
    ])
    setAssistantQuestion('')
    setAssistantLoading(true)
    addAssistantTrace(
      'info',
      `Consulta preparada: “${query}”. ${assistantPreproduction ? 'Se usará el corpus temporal de borradores y revisiones autorizados.' : 'Se usará el corpus publicado y aprobado.'} ${companyDataEnabled ? 'Datos operativos de la empresa activa habilitados.' : 'Datos operativos de la empresa desactivados.'} ${assistantThinking ? 'Think activado para reforzar el razonamiento.' : 'Think desactivado.'} ${streamingEnabled ? 'Streaming activado.' : 'Streaming desactivado.'}`,
    )
    addAssistantTrace(
      'pending',
      companyDataEnabled
        ? 'Solicitud enviada a la IA local. Se recuperará conocimiento autorizado y, si aplica, datos agregados de la empresa activa.'
        : 'Solicitud enviada a la IA local. Esperando la recuperación de conocimiento autorizado.',
    )
    void (async () => {
      try {
        let streamedAnswer = ''
        const result = streamingEnabled
          ? await api.assistantQueryStream(
              query,
              assistantPreproduction,
              assistantThinking,
              (delta) => {
                streamedAnswer += delta
                setAssistantConversation((previous) => previous.map((message) => (
                  message.id === responseId ? { ...message, text: streamedAnswer } : message
                )))
              },
            )
          : await api.assistantQuery(query, assistantPreproduction, assistantThinking)
        setAssistantConversation((previous) => previous.map((message) => (
          message.id === responseId
            ? {
                ...message,
                status: 'complete',
                text: result.answer,
                citations: result.citations,
                companyContext: result.company_context,
                timing: {
                  aiResponseMs: result.timing.ai_response_ms,
                  applicationProcessingMs: result.timing.application_processing_ms,
                  compatibilityProbeMs: result.timing.compatibility_probe_ms,
                  modelGenerationMs: result.timing.model_generation_ms,
                  outputTokens: result.timing.output_tokens,
                  tokensPerSecond: result.timing.tokens_per_second,
                  gatewayPath: result.timing.gateway_path,
                  thinkSent: result.timing.think_sent,
                  think: result.timing.think,
                },
              }
            : message
        )))
        addAssistantTrace(
          'success',
          result.timing.think_sent
            ? `Respuesta recibida en ${formatAssistantDuration(result.timing.ai_response_ms)}: preparación local ${formatAssistantDuration(result.timing.application_processing_ms)}, modelo ${formatAssistantDuration(result.timing.model_generation_ms)}${result.timing.compatibility_probe_ms ? ` y compatibilidad del gateway ${formatAssistantDuration(result.timing.compatibility_probe_ms)}` : ''}. ${result.timing.output_tokens} tokens generados a ${result.timing.tokens_per_second} tokens/s. Think enviado: ${result.timing.think ? 'true' : 'false'}.`
            : result.timing.gateway_path === 'company-data-tool'
              ? 'Respuesta generada con datos operativos agregados de la empresa activa.'
              : result.timing.gateway_path === 'company-data-policy'
                ? 'La consulta se limitó a los datos operativos autorizados de la empresa activa.'
            : result.timing.gateway_path === 'conversation-policy-safety'
              ? 'La solicitud se bloqueó por la política de seguridad; no se consultó la IA local.'
              : result.timing.gateway_path === 'conversation-policy-clarification'
                ? 'Se pidió una aclaración por una consulta ambigua; no se consultó la IA local.'
              : 'Respuesta de cortesía configurada; no se consultó la IA local.',
        )
        addAssistantTrace(
          'success',
          result.citations.length
            ? `Se procesaron ${result.citations.length} fragmento${result.citations.length === 1 ? '' : 's'} recuperado${result.citations.length === 1 ? '' : 's'}.`
            : result.company_context
              ? 'No se requirió conocimiento documental para esta consulta operativa.'
            : 'No se recuperó conocimiento publicado y aprobado para esta incidencia.',
        )
        if (result.company_context)
          addAssistantTrace(
            'success',
            `Se consultaron datos operativos agregados de ${result.company_context.company_name}.`,
          )
        setNotice(
          result.citations.length
            ? `La IA local encontró ${result.citations.length} fragmento${result.citations.length === 1 ? '' : 's'} relevante${result.citations.length === 1 ? '' : 's'}.`
            : result.company_context
              ? 'Respuesta obtenida de datos operativos agregados de la empresa activa.'
            : 'La IA local no encontró contenido publicado y aprobado para esta incidencia.',
        )
      } catch (requestError) {
        const message = errorText(requestError)
        addAssistantTrace('error', `La consulta no se completó: ${message}`)
        setAssistantConversation((previous) => previous.map((entry) => (
          entry.id === responseId
            ? { ...entry, status: 'error', text: `No pude completar la consulta. ${message}` }
            : entry
        )))
      } finally {
        setAssistantLoading(false)
      }
    })()
  }
  const titleInput = (
    <TextField
      size="small"
      label="Título"
      value={form.title}
      onChange={(e) => change('title', e.target.value)}
      disabled={!writable || busy}
      inputProps={{ maxLength: 250 }}
      sx={{
        width: { xs: '100%', sm: 520 },
        '& .MuiOutlinedInput-root': { minHeight: 40 },
        '& .MuiInputBase-input': { py: 1 },
      }}
    />
  )
  const visibleAssistantConversation = assistantConversation.length > 0
    ? assistantConversation
    : [{
        id: 'initial-greeting-preview',
        at: 'Ahora',
        role: 'assistant' as const,
        status: 'complete' as const,
        text: assistantInitialGreeting(configuration),
      }]
  return (
    <Box p={3}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'baseline' }}>
          <Typography variant="h4">Centro de Soluciones</Typography>
          <Typography>Base de conocimiento · Preparación, revisión humana y publicación</Typography>
        </Stack>
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Snackbar
          open={Boolean(notice)}
          autoHideDuration={4500}
          onClose={() => setNotice('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success" variant="filled" onClose={() => setNotice('')} sx={{ minWidth: 280 }}>
            {notice}
          </Alert>
        </Snackbar>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_event, nextTab: KnowledgeTab) => selectTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Secciones del Centro de Soluciones"
            sx={{ px: 1, '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 700 } }}
          >
            <Tab value="documents" label="Gestión documental" disabled={busy} />
            {access.manage_permissions && <Tab value="permissions" label="Permisos editoriales" disabled={busy} />}
            {canManageConfiguration && (
              <Tab value="connections" icon={<SettingsOutlinedIcon fontSize="small" />} iconPosition="start" label="Conexiones" disabled={busy} />
            )}
            {canManageConfiguration && (
              <Tab value="agent" icon={<PsychologyOutlinedIcon fontSize="small" />} iconPosition="start" label="Configuración IA" disabled={busy} />
            )}
            <Tab value="architecture" icon={<AccountTreeOutlinedIcon fontSize="small" />} iconPosition="start" label="Arquitectura" disabled={busy} />
            <Tab value="diagram" icon={<ImageOutlinedIcon fontSize="small" />} iconPosition="start" label="Diagrama" disabled={busy} />
            <Tab value="assistant" icon={<SmartToyOutlinedIcon fontSize="small" />} iconPosition="start" label="Probar IA" disabled={busy} />
          </Tabs>
        </Paper>
        {activeTab === 'assistant' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6">Prueba de incidencia con IA local</Typography>
                <Typography variant="body2" color="text.secondary">
                  Formula una incidencia. {assistantPreproduction
                    ? 'La prueba usa un corpus temporal de borradores y revisiones autorizados.'
                    : 'La prueba recupera únicamente conocimiento publicado y aprobado para IA, según tus permisos.'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 0, lg: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assistantPreproduction}
                      onChange={(event) => setAssistantPreproduction(event.target.checked)}
                      disabled={busy}
                    />
                  }
                  label="Usar borradores y revisiones autorizados para esta prueba"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={assistantThinking}
                      onChange={(event) => setAssistantThinking(event.target.checked)}
                      disabled={busy || assistantLoading}
                    />
                  }
                  label="Reforzar razonamiento (Think)"
                />
                <Chip
                  size="small"
                  color={configuration?.rag.agent_policy.streaming_enabled !== false ? 'primary' : 'default'}
                  variant={configuration?.rag.agent_policy.streaming_enabled !== false ? 'outlined' : 'filled'}
                  label={configuration?.rag.agent_policy.streaming_enabled !== false ? 'Streaming activo' : 'Streaming desactivado'}
                  sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, mt: { xs: 0.5, lg: 0 } }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Desactivado por defecto. Al activarlo, la IA dedica más razonamiento a esta consulta y puede tardar más en responder.
              </Typography>
              {assistantPreproduction && (
                <Alert severity="warning">
                  Corpus temporal de pruebas: el contenido no se publica ni se incorpora al índice productivo de la IA.
                </Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 320px' },
                  gap: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  pt: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle2">Conversación de prueba</Typography>
                    <Button
                      size="small"
                      onClick={initializeAssistantConversation}
                      disabled={busy || assistantLoading}
                    >
                      Inicializar conversación
                    </Button>
                  </Stack>
                  <Stack spacing={1.5} sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                      {visibleAssistantConversation.map((message) => (
                        <Stack
                          key={message.id}
                          direction="row"
                          spacing={1}
                          justifyContent={message.role === 'person' ? 'flex-end' : 'flex-start'}
                        >
                          {message.role === 'assistant' && (
                            <Avatar sx={{ bgcolor: message.status === 'error' ? 'error.main' : 'primary.main', width: 32, height: 32 }}>
                              <SmartToyOutlinedIcon fontSize="small" />
                            </Avatar>
                          )}
                          <Box
                            sx={{
                              maxWidth: { xs: '88%', md: '78%' },
                              px: 1.5,
                              py: 1,
                              borderRadius: 1,
                              border: 1,
                              borderColor: message.role === 'person' ? 'primary.light' : message.status === 'error' ? 'error.light' : 'divider',
                              bgcolor: message.role === 'person'
                                ? 'rgba(25, 118, 210, 0.08)'
                                : message.status === 'error'
                                  ? 'rgba(211, 47, 47, 0.08)'
                                  : 'grey.50',
                              color: 'text.primary',
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {message.role === 'person' ? 'Tú' : 'IA local'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{message.at}</Typography>
                            </Stack>
                            {message.status === 'pending' ? (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                                <LinearProgress sx={{ flex: 1 }} />
                                <Typography variant="body2">{message.text}</Typography>
                              </Stack>
                            ) : message.role === 'assistant' && message.status === 'complete' ? (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  color: 'text.primary',
                                  '& p': { m: 0, mb: 1 },
                                  '& p:last-child': { mb: 0 },
                                  '& ul, & ol': { my: 0.75, pl: 2.5 },
                                  '& li': { mb: 0.4 },
                                  '& h3': { fontSize: '0.95rem', fontWeight: 700, mt: 1.25, mb: 0.6 },
                                }}
                              >
                                <ReactMarkdown
                                  skipHtml
                                  components={{
                                    img: () => null,
                                    a: ({ children }) => <span>{children}</span>,
                                  }}
                                >
                                  {message.text}
                                </ReactMarkdown>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.primary' }}>{message.text}</Typography>
                            )}
                            {message.timing && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                {message.timing.thinkSent
                                  ? `Total del gateway: ${formatAssistantDuration(message.timing.aiResponseMs)} · Modelo: ${formatAssistantDuration(message.timing.modelGenerationMs)} · Preparación local: ${formatAssistantDuration(message.timing.applicationProcessingMs)}${message.timing.compatibilityProbeMs ? ` · Compatibilidad: ${formatAssistantDuration(message.timing.compatibilityProbeMs)}` : ''} · Salida: ${message.timing.outputTokens} tokens (${message.timing.tokensPerSecond} tokens/s) · Think enviado: ${message.timing.think ? 'true' : 'false'}`
                                  : message.timing.gatewayPath === 'company-data-tool'
                                    ? 'Respuesta generada con datos operativos agregados de la empresa activa.'
                                    : message.timing.gatewayPath === 'company-data-policy'
                                      ? 'La consulta se limitó a los datos operativos autorizados de la empresa activa.'
                                  : message.timing.gatewayPath === 'conversation-policy-clarification'
                                    ? 'Se pidió una aclaración por una consulta ambigua; no se consultó la IA local.'
                                    : message.timing.gatewayPath === 'conversation-policy-safety'
                                      ? 'La solicitud se bloqueó por la política de seguridad; no se consultó la IA local.'
                                      : 'Respuesta de cortesía configurada; no se consultó la IA local.'}
                              </Typography>
                            )}
                            {message.companyContext && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                Datos operativos consultados: {message.companyContext.company_name}.
                              </Typography>
                            )}
                            {message.citations && message.citations.length > 0 && (
                              <Stack spacing={0.75} sx={{ mt: 1.25, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>Documentos consultados</Typography>
                                {message.citations.map((citation) => (
                                  <Box key={`${citation.document_id}:${citation.version_id}:${citation.text.slice(0, 24)}`}>
                                    <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>
                                      {citation.title}{citation.score !== null ? ` · ${Math.round(citation.score * 100)}%` : ''}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>{citation.text}</Typography>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </Box>
                          {message.role === 'person' && (
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>T</Avatar>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={5}
                      label="Escribe tu mensaje"
                      placeholder="Por ejemplo: ¿Cuál es el primer paso para crear un proceso?"
                      value={assistantQuestion}
                      onChange={(event) => setAssistantQuestion(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey && assistantQuestion.trim().length >= 3 && !assistantLoading) {
                          event.preventDefault()
                          consultAssistant()
                        }
                      }}
                      inputProps={{ maxLength: 2000 }}
                    />
                    <PrimaryButton
                      type="button"
                      disabled={busy || assistantLoading || assistantQuestion.trim().length < 3}
                      onClick={consultAssistant}
                    >
                      Enviar
                    </PrimaryButton>
                  </Stack>
                </Stack>
                <Box sx={{ borderLeft: { lg: 1 }, borderTop: { xs: 1, lg: 0 }, borderColor: 'divider', pl: { lg: 2 }, pt: { xs: 1.5, lg: 0 } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle2">Actividad</Typography>
                    {assistantTrace.length > 0 && (
                      <Button size="small" onClick={() => setAssistantTrace([])} disabled={busy || assistantLoading}>
                        Limpiar
                      </Button>
                    )}
                  </Stack>
                  {assistantTrace.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      La actividad de la consulta aparecerá aquí.
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mt: 1, maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                      {assistantTrace.map((event) => (
                        <Box
                          key={event.id}
                          sx={{ borderLeft: 2, borderColor: `${event.status === 'error' ? 'error' : event.status === 'success' ? 'success' : 'info'}.main`, pl: 1 }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip
                              size="small"
                              label={event.status === 'pending' ? 'En curso' : event.status === 'success' ? 'Completado' : event.status === 'error' ? 'Error' : 'Preparado'}
                              color={event.status === 'error' ? 'error' : event.status === 'success' ? 'success' : 'info'}
                              variant={event.status === 'pending' ? 'outlined' : 'filled'}
                            />
                            <Typography variant="caption" color="text.secondary">{event.at}</Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>{event.message}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Stack>
          </Paper>
        )}
        {activeTab === 'diagram' && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Diagrama de estado BIA</Typography>
              <Typography variant="body2" color="text.secondary">
                Referencia informativa del estado actual y de los requisitos previos al despliegue en Staging.
              </Typography>
            </Box>
            <Box sx={{ p: 2, overflowX: 'auto', bgcolor: 'background.default' }}>
              <Box
                component="img"
                src="/bia-estado-actual-arquitectura.svg"
                alt="Diagrama del estado actual de la arquitectura BIA"
                sx={{ display: 'block', width: '100%', minWidth: 1050, height: 'auto' }}
              />
            </Box>
          </Paper>
        )}
        {activeTab === 'architecture' && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="h6">Arquitectura del módulo</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recorrido de un documento y de una solicitud de soporte en BIA.
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" label="Flujo operativo" />
              </Stack>
            </Box>
            <Box sx={{ overflowX: 'auto', p: 2 }}>
              <Box
                sx={{
                  minWidth: 1040,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(168px, 1fr) 56px minmax(168px, 1fr) 56px minmax(190px, 1.15fr) 56px minmax(168px, 1fr) 56px minmax(168px, 1fr)',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ArchitectureNode
                  icon={<VerifiedUserOutlinedIcon fontSize="small" />}
                  title="Equipo autorizado"
                  detail="Crea, revisa y publica contenido según sus permisos editoriales."
                  status="Controlado"
                />
                <ArchitectureArrow label="gestiona" />
                <ArchitectureNode
                  icon={<DescriptionOutlinedIcon fontSize="small" />}
                  title="Centro de Soluciones"
                  detail="Valida versiones, audiencia, alcance y aprobación humana."
                  status="Operativo"
                />
                <ArchitectureArrow label="publica" />
                <ArchitectureNode
                  icon={<CloudQueueOutlinedIcon fontSize="small" />}
                  title="Conocimiento privado"
                  detail={
                    privateStorageReady
                      ? 'El original queda en el bucket privado; se genera Markdown derivado para búsqueda y RAG.'
                      : 'El bucket privado todavía no está disponible para almacenar documentos.'
                  }
                  status={privateStorageReady ? 'Protegido' : 'Pendiente'}
                  pending={!privateStorageReady}
                />
                <ArchitectureArrow label="consulta" />
                <ArchitectureNode
                  icon={<PsychologyOutlinedIcon fontSize="small" />}
                  title="IA local"
                  detail={
                    ragReady
                      ? 'Conectada para recuperar únicamente Markdown publicado y elaborar respuestas para BIA.'
                      : ragConfigured
                        ? 'La conexión está configurada, pero la sincronización de conocimiento está desactivada.'
                        : 'Faltan la conexión, API Key o modelo para consultar la base de conocimiento.'
                  }
                  status={ragReady ? 'Activa' : ragConfigured ? 'Configurada' : 'Pendiente'}
                  pending={!ragReady}
                />
                <ArchitectureArrow label="sin solución" />
                <ArchitectureNode
                  icon={<SupportAgentOutlinedIcon fontSize="small" />}
                  title="TAS"
                  detail={
                    tasReady
                      ? 'Crea tareas para el servicio técnico cuando la IA no pueda resolver una solicitud.'
                      : tasConfigured
                        ? 'La conexión está configurada, pero el escalado automático está desactivado.'
                        : 'Pendiente de configurar la API del TAS, el pedido de destino y el escalado automático.'
                  }
                  status={tasReady ? 'Activo' : tasConfigured ? 'Configurado' : 'Pendiente'}
                  pending={!tasConfigured}
                />
              </Box>
            </Box>
            <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Typography variant="body2" color="text.secondary">
                Estado consultado al abrir esta vista. Los documentos no se exponen públicamente y los bloques se
                actualizan según la configuración efectiva del servidor.
              </Typography>
            </Box>
          </Paper>
        )}
        {activeTab === 'connections' && configuration && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="h6">Conexiones</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configura y comprueba cada servicio por separado.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={operationalStatus.color}
                  label={operationalStatus.label}
                />
              </Stack>
              <Tabs
                value={connectionTab}
                onChange={(_event, nextTab: ConnectionTab) => setConnectionTab(nextTab)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Conexiones del Centro de Soluciones"
                sx={{ mt: 1.5, minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none' } }}
              >
                <Tab value="operational" label="Comprobar operativa" />
                <Tab value="storage" label="Almacenamiento" />
                <Tab value="database" label="Base de datos" />
                <Tab value="rag" label="IA local" />
                <Tab value="tas" label="TAS" />
              </Tabs>
              {connectionTab === 'operational' && configuration.source !== 'LOCAL_PREVIEW' && (
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  <PrimaryButton
                    leadingIcon={<FactCheckOutlinedIcon fontSize="small" />}
                    disabled={busy}
                    onClick={() => void runOperationalChecks()}
                  >
                    Comprobar configuración operativa
                  </PrimaryButton>
                  {operationalChecks && (
                    <Stack spacing={0.75} aria-live="polite">
                      {operationalChecks.map((check) => (
                        <Stack
                          key={check.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={0.75}
                        >
                          <Chip
                            size="small"
                            color={
                              check.state === 'success'
                                ? 'success'
                                : check.state === 'error'
                                  ? 'error'
                                  : check.state === 'running'
                                    ? 'primary'
                                    : 'default'
                            }
                            label={
                              check.state === 'running'
                                ? `${check.label}: comprobando...`
                                : check.label
                            }
                            variant={check.state === 'pending' ? 'outlined' : 'filled'}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {check.detail}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
            {connectionTab === 'storage' && <ConfigurationSection
              title="Documentos y almacenamiento"
              status={
                <Chip
                  size="small"
                  color={configuration.resources.private_storage_enabled ? 'success' : 'default'}
                  label={configuration.resources.private_storage_enabled ? 'Protegido' : 'Pendiente'}
                  variant={configuration.resources.private_storage_enabled ? 'filled' : 'outlined'}
                />
              }
            >
              <Stack divider={<Divider flexItem />}>
                <ConfigurationValue
                  label="Almacenamiento privado"
                  value={yesNo(configuration.resources.private_storage_enabled)}
                />
                <ConfigurationValue
                  label="Bucket dedicado"
                  value={yesNo(configuration.resources.bucket_configured)}
                />
                <ConfigurationValue label="Tamaño máximo por archivo" value={`${configuration.resources.max_size_mib} MiB`} />
                <ConfigurationValue
                  label="Tipos aceptados"
                  value={configuration.resources.allowed_types.map((type) => type.label).join(', ')}
                />
              </Stack>
              {configuration.source !== 'LOCAL_PREVIEW' && (
                <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <PrimaryButton
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        try {
                          const result = await api.storageConnection()
                          setStorageConnectionError('')
                          setStorageConnectionResult(
                            `Almacenamiento AWS correcto. El bucket privado ${result.bucket} cumple las protecciones requeridas.`,
                          )
                        } catch (requestError) {
                          setStorageConnectionResult('')
                          setStorageConnectionError(errorText(requestError))
                        }
                      })
                    }
                  >
                    Comprobar almacenamiento AWS
                  </PrimaryButton>
                  {storageConnectionResult && <Alert severity="success">{storageConnectionResult}</Alert>}
                  {storageConnectionError && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            run(async () => {
                              try {
                                const result = await api.storageConnection()
                                setStorageConnectionError('')
                                setStorageConnectionResult(
                                  `Almacenamiento AWS correcto. El bucket privado ${result.bucket} cumple las protecciones requeridas.`,
                                )
                              } catch (requestError) {
                                setStorageConnectionResult('')
                                setStorageConnectionError(errorText(requestError))
                              }
                            })
                          }
                        >
                          Reintentar
                        </Button>
                      }
                    >
                      {storageConnectionError}
                      {storageConnectionError.includes('sesión de AWS') && (
                        <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5 }}>
                          Bluesite no guarda claves AWS. En desarrollo usa la sesión temporal del AWS CLI; en producción debe usarse un rol IAM de la carga de trabajo.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Stack>
              )}
            </ConfigurationSection>}
            {connectionTab === 'database' && <ConfigurationSection
              title="Base de datos"
              defaultExpanded
              status={
                <Chip
                  size="small"
                  color={configuration.database.configured && configuration.database.accessible ? 'success' : 'default'}
                  label={configuration.database.configured && configuration.database.accessible ? 'Accesible' : 'Pendiente'}
                  variant={configuration.database.configured && configuration.database.accessible ? 'filled' : 'outlined'}
                />
              }
            >
              <Stack spacing={2}>
                <Stack divider={<Divider flexItem />}>
                  <ConfigurationValue
                    label="Conexión de Bluesite"
                    value={configuration.database.configured && configuration.database.accessible ? 'Configurada y accesible' : 'Pendiente de comprobar'}
                  />
                  <ConfigurationValue label="Alcance de la IA" value="Solo la empresa activa de la sesión" />
                  <ConfigurationValue label="Datos permitidos" value="Datos operativos agregados, sin datos personales" />
                </Stack>
                {configuration.source === 'LOCAL_PREVIEW' ? (
                  <Alert severity="warning">
                    La comprobación de base de datos estará disponible al actualizar el backend local.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    <PrimaryButton
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          try {
                            const result = await api.databaseConnection()
                            setDatabaseConnectionError('')
                            setDatabaseConnectionResult(
                              `Base de datos ${result.source} accesible. La IA solo podrá consultar datos agregados de la empresa activa.`,
                            )
                          } catch (requestError) {
                            setDatabaseConnectionResult('')
                            setDatabaseConnectionError(errorText(requestError))
                          }
                        })
                      }
                    >
                      Comprobar base de datos
                    </PrimaryButton>
                    {databaseConnectionResult && <Alert severity="success">{databaseConnectionResult}</Alert>}
                    {databaseConnectionError && <Alert severity="error">{databaseConnectionError}</Alert>}
                  </Stack>
                )}
              </Stack>
            </ConfigurationSection>}
            {connectionTab === 'rag' && <ConfigurationSection
              title="IA local y base de conocimiento"
              defaultExpanded
              status={
                <Chip
                  size="small"
                  color={configuration.rag.enabled ? 'success' : 'default'}
                  label={configuration.rag.enabled ? 'Activa' : 'Pendiente'}
                  variant={configuration.rag.enabled ? 'filled' : 'outlined'}
                />
              }
            >
              <Stack divider={<Divider flexItem />}>
                <ConfigurationValue label="Sincronización RAG" value={yesNo(configuration.rag.enabled)} />
                <ConfigurationValue label="Gateway" value={configuredValue(configuration.rag.gateway)} />
                <ConfigurationValue label="Modelo" value={configuredValue(configuration.rag.model_id)} />
                <ConfigurationValue
                  label="Credencial del servicio"
                  value={configuration.rag.api_key_configured ? 'Configurada' : 'Pendiente'}
                />
                <ConfigurationValue
                  label="mTLS"
                  value={
                    configuration.rag.mtls_required
                      ? configuration.rag.mtls_configured
                        ? 'Requerido y configurado'
                        : 'Requerido, pendiente de configurar'
                      : 'No requerido'
                  }
                />
                <ConfigurationValue
                  label="Límite de espera"
                  value={`${configuration.rag.timeout_ms / 1000} segundos`}
                />
                <ConfigurationValue label="Política de respuesta" value="Gestionada en Configuración IA" />
              </Stack>
              {configuration.source === 'LOCAL_PREVIEW' ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  El servidor local todavía usa la versión anterior. La edición estará disponible al actualizar el
                  backend.
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Conexión de la IA local
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      La clave se guarda cifrada y no vuelve a mostrarse después de guardarla.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ragForm.enabled}
                        disabled={busy}
                        onChange={(event) => setRagForm({ ...ragForm, enabled: event.target.checked })}
                      />
                    }
                    label="Activar sincronización de conocimiento"
                  />
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agentPolicyForm.companyDataEnabled}
                          disabled={busy || !configuration.database.configured || !configuration.database.accessible}
                          onChange={(event) => setAgentPolicyForm({
                            ...agentPolicyForm,
                            companyDataEnabled: event.target.checked,
                          })}
                        />
                      }
                      label="Permitir a la IA local consultar la base de datos de la empresa activa"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {!configuration.database.configured || !configuration.database.accessible
                        ? 'Primero configura y comprueba la conexión en Base de datos.'
                        : 'Desactivado por defecto. Solo usa datos agregados de la compañía incluida en la sesión del administrador; nunca datos personales, resultados individuales ni información de otras empresas.'}
                    </Typography>
                  </Box>
                  <TextField
                    label="Dirección de la IA local"
                    placeholder="http://190.25.43.58:3000"
                    value={ragForm.gateway}
                    disabled={busy}
                    onChange={(event) => {
                      const gateway = event.target.value.replace(/\\/g, '/')
                      if (/^sk-[A-Za-z0-9_-]+$/.test(gateway.trim())) {
                        setError('La API Key debe introducirse en su campo protegido, no en la URL del gateway.')
                        return
                      }
                      setRagConnectionModels(null)
                      setRagConnectionError('')
                      setRagForm({ ...ragForm, gateway })
                    }}
                    helperText="Admite HTTP o HTTPS. Usa la dirección publicada de la IA local, sin rutas adicionales."
                    fullWidth
                  />
                  {ragForm.gateway.trim().startsWith('http://') && (
                    <Alert severity="warning">
                      HTTP se admite para la preproducción temporal, pero expone la API Key en tránsito. Publica HTTPS antes de Producción.
                    </Alert>
                  )}
                  <TextField
                    label={configuration.rag.api_key_configured ? 'Reemplazar API Key' : 'API Key'}
                    type={showRagApiKey ? 'text' : 'password'}
                    value={ragForm.apiKey}
                    disabled={busy}
                    onChange={(event) => {
                      setRagModels([])
                      setRagConnectionModels(null)
                      setRagConnectionError('')
                      setRagForm({ ...ragForm, apiKey: event.target.value })
                    }}
                    onBlur={() => void readRagModels()}
                    helperText={
                      configuration.rag.api_key_configured
                        ? 'Déjala vacía para conservar la API Key actual.'
                        : 'Crea una API Key para el usuario técnico; no uses un token de sesión.'
                    }
                    inputProps={{ autoComplete: 'new-password' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showRagApiKey ? 'Ocultar API Key' : 'Mostrar API Key'}
                            edge="end"
                            onClick={() => setShowRagApiKey((visible) => !visible)}
                            onMouseDown={(event) => event.preventDefault()}
                          >
                            {showRagApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  <PrimaryButton
                    disabled={busy}
                    onClick={() => void readRagModels()}
                  >
                    Comprobar IA y cargar modelos disponibles
                  </PrimaryButton>
                  {!ragForm.gateway.trim() && (
                    <Typography variant="body2" color="text.secondary">
                      Introduce la dirección de la IA local para comprobar la conexión y cargar los modelos.
                    </Typography>
                  )}
                  {ragConnectionModels !== null && (
                    <Alert severity="success">
                      Conexión con la IA local correcta. Se encontraron {ragConnectionModels} modelos.
                    </Alert>
                  )}
                  {ragConnectionError && <Alert severity="error">{ragConnectionError}</Alert>}
                  {ragModels.length ? (
                    <TextField
                      select
                      label="Modelo de IA local"
                      value={ragForm.modelId}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, modelId: event.target.value })}
                      helperText="Listado obtenido con la API Key actual."
                      fullWidth
                    >
                      {ragModels.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          {model.name === model.id ? model.id : `${model.name} (${model.id})`}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label="Modelo de IA local"
                      value={ragForm.modelId}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, modelId: event.target.value })}
                      helperText="La lista se cargará al introducir la API Key."
                      fullWidth
                    />
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Espera (ms)"
                      type="number"
                      value={ragForm.timeoutMs}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, timeoutMs: Number(event.target.value) })}
                      inputProps={{ min: 1000, max: 60000 }}
                      fullWidth
                    />
                    <TextField
                      label="Lote"
                      type="number"
                      value={ragForm.batchSize}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, batchSize: Number(event.target.value) })}
                      inputProps={{ min: 1, max: 100 }}
                      fullWidth
                    />
                    <TextField
                      label="Sondeo (ms)"
                      type="number"
                      value={ragForm.pollMs}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, pollMs: Number(event.target.value) })}
                      inputProps={{ min: 1000, max: 60000 }}
                      fullWidth
                    />
                    <TextField
                      label="Reintentos"
                      type="number"
                      value={ragForm.maxAttempts}
                      disabled={busy}
                      onChange={(event) => setRagForm({ ...ragForm, maxAttempts: Number(event.target.value) })}
                      inputProps={{ min: 1, max: 100 }}
                      fullWidth
                    />
                  </Stack>
                  <PrimaryButton
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const updated = await api.updateConfiguration({
                          rag_enabled: ragForm.enabled,
                          rag_gateway_url: ragForm.gateway,
                          rag_model_id: ragForm.modelId,
                          ...(ragForm.apiKey ? { rag_api_key: ragForm.apiKey } : {}),
                          rag_timeout_ms: ragForm.timeoutMs,
                          rag_batch_size: ragForm.batchSize,
                          rag_poll_ms: ragForm.pollMs,
                          rag_max_attempts: ragForm.maxAttempts,
                          rag_response_audience: agentPolicyForm.audience,
                          rag_response_style: agentPolicyForm.style,
                          rag_response_instructions: agentPolicyForm.instructions,
                          rag_agent_role: agentPolicyForm.role,
                          rag_response_format: agentPolicyForm.responseFormat,
                          rag_detail_level: agentPolicyForm.detailLevel,
                          rag_source_priority: agentPolicyForm.sourcePriority,
                          rag_min_confidence: agentPolicyForm.minConfidence,
                          rag_clarification_mode: agentPolicyForm.clarificationMode,
                          rag_citations_required: agentPolicyForm.citationsRequired,
                          rag_require_evidence: agentPolicyForm.requireEvidence,
                          rag_agent_rules: agentPolicyForm.rules,
                          rag_internet_enabled: agentPolicyForm.internetEnabled,
                          rag_company_data_enabled: agentPolicyForm.companyDataEnabled,
                          rag_streaming_enabled: agentPolicyForm.streamingEnabled,
                          rag_max_tokens: agentPolicyForm.maxTokens,
                          rag_courtesy_enabled: agentPolicyForm.courtesyEnabled,
                          rag_greeting_formality: agentPolicyForm.greetingFormality,
                          rag_farewell_formality: agentPolicyForm.farewellFormality,
                          rag_greeting_intent: agentPolicyForm.greetingIntent,
                          rag_greeting_response: agentPolicyForm.greetingResponse,
                          rag_thanks_response: agentPolicyForm.thanksResponse,
                          rag_farewell_response: agentPolicyForm.farewellResponse,
                          rag_safety_response: agentPolicyForm.safetyResponse,
                          rag_unrecognized_response: agentPolicyForm.unrecognizedResponse,
                          rag_blocked_terms: agentPolicyForm.blockedTerms,
                          tas_enabled: tasForm.enabled,
                          tas_api_base_url: tasForm.endpoint,
                          ...(tasForm.apiKey ? { tas_api_key: tasForm.apiKey } : {}),
                          tas_timeout_ms: tasForm.timeoutMs,
                          tas_pedido_id: tasForm.pedidoId,
                          tas_created_by_employee_id: tasForm.createdByEmployeeId,
                          tas_responsible_employee_id: tasForm.responsibleEmployeeId,
                        })
                        setConfiguration(updated)
                        setRagForm(ragFormFrom(updated))
                        setAgentPolicyForm(agentPolicyFormFrom(updated))
                        setTasForm(tasFormFrom(updated))
                        setNotice('Configuración de IA guardada.')
                      })
                    }
                  >
                    Guardar conexión de IA
                  </PrimaryButton>
                </Stack>
              )}
            </ConfigurationSection>}
            {connectionTab === 'tas' && <ConfigurationSection
              title="Escalado al TAS"
              status={
                <Chip
                  size="small"
                  color={
                    configuration.tas.enabled ||
                    (configuration.tas.endpoint &&
                      configuration.tas.api_key_configured &&
                      configuration.tas.pedido_id &&
                      configuration.tas.created_by_employee_id &&
                      configuration.tas.responsible_employee_id)
                      ? 'success'
                      : 'default'
                  }
                  label={
                    configuration.tas.enabled
                      ? 'Activo'
                      : configuration.tas.endpoint &&
                          configuration.tas.api_key_configured &&
                          configuration.tas.pedido_id &&
                          configuration.tas.created_by_employee_id &&
                          configuration.tas.responsible_employee_id
                        ? 'Configurado'
                        : 'Pendiente'
                  }
                  variant={
                    configuration.tas.enabled ||
                    (configuration.tas.endpoint &&
                      configuration.tas.api_key_configured &&
                      configuration.tas.pedido_id &&
                      configuration.tas.created_by_employee_id &&
                      configuration.tas.responsible_employee_id)
                      ? 'filled'
                      : 'outlined'
                  }
                />
              }
            >
              <Stack divider={<Divider flexItem />}>
                <ConfigurationValue label="Escalado automático" value={yesNo(configuration.tas.enabled)} />
                <ConfigurationValue label="Conexión TAS" value={configuredValue(configuration.tas.endpoint)} />
                <ConfigurationValue
                  label="Credencial del servicio"
                  value={configuration.tas.api_key_configured ? 'Configurada' : 'Pendiente'}
                />
                <ConfigurationValue label="Pedido de destino" value={configuredValue(configuration.tas.pedido_id)} />
                <ConfigurationValue
                  label="Responsable"
                  value={configuredValue(configuration.tas.responsible_employee_id)}
                />
                <ConfigurationValue label="Estado inicial" value={configuration.tas.initial_state} />
              </Stack>
              {configuration.source !== 'LOCAL_PREVIEW' && (
                <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Conexión del TAS
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tasForm.enabled}
                        disabled={busy}
                        onChange={(event) => setTasForm({ ...tasForm, enabled: event.target.checked })}
                      />
                    }
                    label="Activar escalado automático al TAS"
                  />
                  <TextField
                    label="Dirección de la API TAS"
                    placeholder="https://seidel_tasv3.apiseidel.es:5000"
                    value={tasForm.endpoint}
                    disabled={busy}
                    onChange={(event) => setTasForm({ ...tasForm, endpoint: event.target.value })}
                    helperText="Debe usar HTTPS. Se conserva cualquier ruta base configurada por el API."
                    fullWidth
                  />
                  <TextField
                    label={configuration.tas.api_key_configured ? 'Reemplazar API Key TAS' : 'API Key TAS'}
                    type={showTasApiKey ? 'text' : 'password'}
                    value={tasForm.apiKey}
                    disabled={busy}
                    onChange={(event) => setTasForm({ ...tasForm, apiKey: event.target.value })}
                    helperText={
                      configuration.tas.api_key_configured
                        ? 'Déjala vacía para conservar la API Key actual.'
                        : 'La API Key se cifra antes de guardarse.'
                    }
                    inputProps={{ autoComplete: 'new-password' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showTasApiKey ? 'Ocultar API Key TAS' : 'Mostrar API Key TAS'}
                            edge="end"
                            onClick={() => setShowTasApiKey((visible) => !visible)}
                            onMouseDown={(event) => event.preventDefault()}
                          >
                            {showTasApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="ID pedido de destino"
                      type="number"
                      value={tasForm.pedidoId ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setTasForm({
                          ...tasForm,
                          pedidoId: event.target.value ? Number(event.target.value) : null,
                        })
                      }
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                    <TextField
                      label="ID empleado creador"
                      type="number"
                      value={tasForm.createdByEmployeeId ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setTasForm({
                          ...tasForm,
                          createdByEmployeeId: event.target.value ? Number(event.target.value) : null,
                        })
                      }
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                    <TextField
                      label="ID responsable"
                      type="number"
                      value={tasForm.responsibleEmployeeId ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setTasForm({
                          ...tasForm,
                          responsibleEmployeeId: event.target.value ? Number(event.target.value) : null,
                        })
                      }
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                    <TextField
                      label="Espera (ms)"
                      type="number"
                      value={tasForm.timeoutMs}
                      disabled={busy}
                      onChange={(event) => setTasForm({ ...tasForm, timeoutMs: Number(event.target.value) })}
                      inputProps={{ min: 1000, max: 60000 }}
                      fullWidth
                    />
                  </Stack>
                  <PrimaryButton
                    disabled={busy}
                    onClick={() => {
                      if (!tasForm.endpoint.trim() || !tasForm.pedidoId) {
                        const message = 'Introduce la dirección de la API TAS y el ID del pedido de destino para comprobar la conexión.'
                        setTasConnectionResult('')
                        setTasConnectionError(message)
                        setError(message)
                        return
                      }
                      void run(async () => {
                        const result = await api.tasConnection({
                          tas_api_base_url: tasForm.endpoint,
                          ...(tasForm.apiKey ? { tas_api_key: tasForm.apiKey } : {}),
                          tas_timeout_ms: tasForm.timeoutMs,
                          tas_pedido_id: tasForm.pedidoId as number,
                        })
                        setTasConnectionError('')
                        setTasConnectionResult(`Conexión con el TAS correcta. Se verificó el acceso al pedido ${result.pedido_id}.`)
                      })
                    }}
                  >
                    Comprobar conexión TAS
                  </PrimaryButton>
                  {tasConnectionResult && <Alert severity="success">{tasConnectionResult}</Alert>}
                  {tasConnectionError && <Alert severity="error">{tasConnectionError}</Alert>}
                  <TextField label="Estado inicial" value="Pendiente" disabled fullWidth />
                  <PrimaryButton
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const updated = await api.updateConfiguration({
                          rag_enabled: ragForm.enabled,
                          rag_gateway_url: ragForm.gateway,
                          rag_model_id: ragForm.modelId,
                          ...(ragForm.apiKey ? { rag_api_key: ragForm.apiKey } : {}),
                          rag_timeout_ms: ragForm.timeoutMs,
                          rag_batch_size: ragForm.batchSize,
                          rag_poll_ms: ragForm.pollMs,
                          rag_max_attempts: ragForm.maxAttempts,
                          rag_response_audience: agentPolicyForm.audience,
                          rag_response_style: agentPolicyForm.style,
                          rag_response_instructions: agentPolicyForm.instructions,
                          rag_agent_role: agentPolicyForm.role,
                          rag_response_format: agentPolicyForm.responseFormat,
                          rag_detail_level: agentPolicyForm.detailLevel,
                          rag_source_priority: agentPolicyForm.sourcePriority,
                          rag_min_confidence: agentPolicyForm.minConfidence,
                          rag_clarification_mode: agentPolicyForm.clarificationMode,
                          rag_citations_required: agentPolicyForm.citationsRequired,
                          rag_require_evidence: agentPolicyForm.requireEvidence,
                          rag_agent_rules: agentPolicyForm.rules,
                          rag_internet_enabled: agentPolicyForm.internetEnabled,
                          rag_company_data_enabled: agentPolicyForm.companyDataEnabled,
                          rag_streaming_enabled: agentPolicyForm.streamingEnabled,
                          rag_max_tokens: agentPolicyForm.maxTokens,
                          rag_courtesy_enabled: agentPolicyForm.courtesyEnabled,
                          rag_greeting_formality: agentPolicyForm.greetingFormality,
                          rag_farewell_formality: agentPolicyForm.farewellFormality,
                          rag_greeting_intent: agentPolicyForm.greetingIntent,
                          rag_greeting_response: agentPolicyForm.greetingResponse,
                          rag_thanks_response: agentPolicyForm.thanksResponse,
                          rag_farewell_response: agentPolicyForm.farewellResponse,
                          rag_safety_response: agentPolicyForm.safetyResponse,
                          rag_unrecognized_response: agentPolicyForm.unrecognizedResponse,
                          rag_blocked_terms: agentPolicyForm.blockedTerms,
                          tas_enabled: tasForm.enabled,
                          tas_api_base_url: tasForm.endpoint,
                          ...(tasForm.apiKey ? { tas_api_key: tasForm.apiKey } : {}),
                          tas_timeout_ms: tasForm.timeoutMs,
                          tas_pedido_id: tasForm.pedidoId,
                          tas_created_by_employee_id: tasForm.createdByEmployeeId,
                          tas_responsible_employee_id: tasForm.responsibleEmployeeId,
                        })
                        setConfiguration(updated)
                        setRagForm(ragFormFrom(updated))
                        setAgentPolicyForm(agentPolicyFormFrom(updated))
                        setTasForm(tasFormFrom(updated))
                        setNotice('Configuración del TAS guardada.')
                      })
                    }
                  >
                    Guardar conexión TAS
                  </PrimaryButton>
                </Stack>
              )}
            </ConfigurationSection>}
          </Paper>
        )}
        {activeTab === 'agent' && configuration && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">Configuración del agente de IA</Typography>
                <Typography variant="body2" color="text.secondary">
                  Define cómo recupera, contrasta y redacta la información. Estas reglas no sustituyen a los documentos autorizados.
                </Typography>
              </Box>
              <Alert severity="info">
                El agente no inventa hechos: cuando la evidencia no alcance el umbral configurado, pedirá aclaración o declarará el límite de la respuesta.
              </Alert>
              <Tabs
                value={agentConfigTab}
                onChange={(_event, value: AgentConfigTab) => setAgentConfigTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Secciones de configuración del agente"
              >
                <Tab value="response" label="Respuesta" />
                <Tab value="sources" label="Fuentes y datos" />
                <Tab value="conversation" label="Conversación" />
                <Tab value="security" label="Seguridad" />
              </Tabs>
              {agentConfigTab === 'conversation' && (
                <Stack spacing={2} component="section" aria-labelledby="agent-conversation-heading">
                  <Box>
                    <Typography id="agent-conversation-heading" variant="subtitle1" fontWeight={700}>Conversación</Typography>
                    <Typography variant="body2" color="text.secondary">Saludo, despedida y respuestas de cortesía.</Typography>
                  </Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={agentPolicyForm.courtesyEnabled}
                    disabled={busy}
                    onChange={(event) => setAgentPolicyForm({
                      ...agentPolicyForm,
                      courtesyEnabled: event.target.checked,
                    })}
                  />
                )}
                label="Responder saludos, agradecimientos y despedidas sin consultar documentos"
              />
              <TextField
                label="Saludo inicial"
                value={agentPolicyForm.greetingIntent}
                disabled={busy || !agentPolicyForm.courtesyEnabled}
                onChange={(event) => setAgentPolicyForm({
                  ...agentPolicyForm,
                  greetingIntent: event.target.value,
                })}
                helperText="Es el primer mensaje que verá la persona al abrir o reiniciar Probar IA."
                inputProps={{ maxLength: 4000 }}
                minRows={2}
                multiline
                fullWidth
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                <Box sx={{ minWidth: 0, px: 1, pb: 1, width: '100%', overflow: 'hidden' }}>
                  <Typography variant="subtitle2">Estilo esperado de saludo</Typography>
                  <Slider
                    value={agentPolicyForm.greetingFormality}
                    min={0}
                    max={100}
                    step={5}
                    marks={[{ value: 0 }, { value: 50 }, { value: 100 }]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    disabled={busy || !agentPolicyForm.courtesyEnabled}
                    onChange={(_event, value) => setAgentPolicyForm({
                      ...agentPolicyForm,
                      greetingFormality: value as number,
                    })}
                    aria-label="Estilo esperado de saludo"
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5, mt: 0.25 }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}>Muy cercano</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', textAlign: 'center', fontSize: '0.72rem' }}>Neutro</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', textAlign: 'right', fontSize: '0.72rem' }}>Muy formal</Typography>
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0, px: 1, pb: 1, width: '100%', overflow: 'hidden' }}>
                  <Typography variant="subtitle2">Estilo esperado de despedida</Typography>
                  <Slider
                    value={agentPolicyForm.farewellFormality}
                    min={0}
                    max={100}
                    step={5}
                    marks={[{ value: 0 }, { value: 50 }, { value: 100 }]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    disabled={busy || !agentPolicyForm.courtesyEnabled}
                    onChange={(_event, value) => setAgentPolicyForm({
                      ...agentPolicyForm,
                      farewellFormality: value as number,
                    })}
                    aria-label="Estilo esperado de despedida"
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5, mt: 0.25 }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}>Muy cercana</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', textAlign: 'center', fontSize: '0.72rem' }}>Neutra</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', textAlign: 'right', fontSize: '0.72rem' }}>Muy formal</Typography>
                  </Box>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  label="Respuesta para saludos"
                  value={agentPolicyForm.greetingResponse}
                  disabled={busy || !agentPolicyForm.courtesyEnabled}
                  onChange={(event) => setAgentPolicyForm({
                    ...agentPolicyForm,
                    greetingResponse: event.target.value,
                  })}
                  inputProps={{ maxLength: 1000 }}
                  minRows={2}
                  multiline
                  fullWidth
                />
                <TextField
                  label="Respuesta para agradecimientos"
                  value={agentPolicyForm.thanksResponse}
                  disabled={busy || !agentPolicyForm.courtesyEnabled}
                  onChange={(event) => setAgentPolicyForm({
                    ...agentPolicyForm,
                    thanksResponse: event.target.value,
                  })}
                  inputProps={{ maxLength: 1000 }}
                  minRows={2}
                  multiline
                  fullWidth
                />
              </Stack>
              <TextField
                label="Respuesta para despedidas"
                value={agentPolicyForm.farewellResponse}
                disabled={busy || !agentPolicyForm.courtesyEnabled}
                onChange={(event) => setAgentPolicyForm({
                  ...agentPolicyForm,
                  farewellResponse: event.target.value,
                })}
                inputProps={{ maxLength: 1000 }}
                minRows={2}
                multiline
                fullWidth
              />
                </Stack>
              )}
              {agentConfigTab === 'security' && (
                <Stack spacing={2} component="section" aria-labelledby="agent-security-heading">
                  <Box>
                    <Typography id="agent-security-heading" variant="subtitle1" fontWeight={700}>Seguridad</Typography>
                    <Typography variant="body2" color="text.secondary">Protección y criterios de rechazo del agente.</Typography>
                  </Box>
                  <Alert severity="success">
                    La protección base siempre bloquea lenguaje ofensivo o solicitudes perjudiciales antes de consultar la base de conocimiento.
                  </Alert>
              <TextField
                label="Respuesta de seguridad"
                value={agentPolicyForm.safetyResponse}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({
                  ...agentPolicyForm,
                  safetyResponse: event.target.value,
                })}
                helperText="Se muestra al detectar lenguaje ofensivo o una petición perjudicial. La protección base no se puede desactivar."
                inputProps={{ maxLength: 1000 }}
                minRows={2}
                multiline
                fullWidth
              />
              <TextField
                label="Términos adicionales bloqueados"
                value={agentPolicyForm.blockedTerms}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({
                  ...agentPolicyForm,
                  blockedTerms: event.target.value,
                })}
                helperText="Separa los términos o expresiones con comas, punto y coma o saltos de línea."
                inputProps={{ maxLength: 2000 }}
                minRows={2}
                multiline
                fullWidth
              />
                </Stack>
              )}
              {agentConfigTab === 'response' && (
                <Stack spacing={2} component="section" aria-labelledby="agent-response-heading">
                  <Box>
                    <Typography id="agent-response-heading" variant="subtitle1" fontWeight={700}>Diseño de respuesta</Typography>
                    <Typography variant="body2" color="text.secondary">Rol, formato, extensión y pautas de redacción.</Typography>
                  </Box>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  select
                  label="Rol del agente"
                  value={agentPolicyForm.role}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, role: event.target.value as AgentPolicyForm['role'] })}
                  fullWidth
                >
                  <MenuItem value="SUPPORT_SPECIALIST">Especialista de soporte funcional</MenuItem>
                  <MenuItem value="HR_ADVISOR">Asesor de RR. HH.</MenuItem>
                  <MenuItem value="PLATFORM_ADMIN">Administrador de plataforma</MenuItem>
                  <MenuItem value="END_USER_GUIDE">Guía para persona usuaria final</MenuItem>
                </TextField>
                <TextField
                  label="Perfil destinatario"
                  value={agentPolicyForm.audience}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, audience: event.target.value })}
                  inputProps={{ maxLength: 200 }}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  select
                  label="Formato de respuesta"
                  value={agentPolicyForm.responseFormat}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, responseFormat: event.target.value as AgentPolicyForm['responseFormat'] })}
                  fullWidth
                >
                  <MenuItem value="STEP_BY_STEP">Pasos guiados</MenuItem>
                  <MenuItem value="INCIDENT_DIAGNOSIS">Diagnóstico de incidencia</MenuItem>
                  <MenuItem value="CHECKLIST">Lista de comprobación</MenuItem>
                  <MenuItem value="EXPLANATION">Explicación contextual</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Nivel de detalle"
                  value={agentPolicyForm.detailLevel}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, detailLevel: event.target.value as AgentPolicyForm['detailLevel'] })}
                  fullWidth
                >
                  <MenuItem value="BRIEF">Breve</MenuItem>
                  <MenuItem value="STANDARD">Estándar</MenuItem>
                  <MenuItem value="DETAILED">Detallado</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Estilo de redacción"
                  value={agentPolicyForm.style}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, style: event.target.value as AgentPolicyForm['style'] })}
                  fullWidth
                >
                  <MenuItem value="PRACTICAL">Orientación práctica</MenuItem>
                  <MenuItem value="BRIEF">Respuesta breve</MenuItem>
                  <MenuItem value="TECHNICAL">Explicación técnica</MenuItem>
                  <MenuItem value="SUPPORT">Atención de incidencias</MenuItem>
                </TextField>
                <TextField
                  label="Máximo de tokens"
                  type="number"
                  value={agentPolicyForm.maxTokens}
                  disabled={busy}
                  onChange={(event) => setAgentPolicyForm({
                    ...agentPolicyForm,
                    maxTokens: Math.max(64, Math.min(4096, Number(event.target.value) || 64)),
                  })}
                  helperText="Límite de salida. Menos tokens suele reducir el tiempo de respuesta."
                  inputProps={{ min: 64, max: 4096, step: 64 }}
                  fullWidth
                />
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={agentPolicyForm.streamingEnabled}
                    disabled={busy}
                    onChange={(event) => setAgentPolicyForm({
                      ...agentPolicyForm,
                      streamingEnabled: event.target.checked,
                    })}
                  />
                }
                label="Mostrar la respuesta en streaming"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Activo por defecto. La respuesta se irá mostrando a medida que la IA la genere.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agentPolicyForm.style === 'BRIEF' && agentPolicyForm.detailLevel === 'BRIEF'}
                    disabled={busy}
                    onChange={(event) => setAgentPolicyForm({
                      ...agentPolicyForm,
                      style: event.target.checked ? 'BRIEF' : 'PRACTICAL',
                      detailLevel: event.target.checked ? 'BRIEF' : 'STANDARD',
                    })}
                  />
                }
                label="Priorizar respuesta breve"
              />
              <TextField
                label="Pautas editoriales"
                value={agentPolicyForm.instructions}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, instructions: event.target.value })}
                helperText="Tono, estructura y advertencias para la respuesta final."
                inputProps={{ maxLength: 4000 }}
                minRows={3}
                multiline
                fullWidth
              />
                </Stack>
              )}
              {agentConfigTab === 'sources' && (
                <Stack spacing={2} component="section" aria-labelledby="agent-sources-heading">
                  <Box>
                    <Typography id="agent-sources-heading" variant="subtitle1" fontWeight={700}>Fuentes y datos</Typography>
                    <Typography variant="body2" color="text.secondary">Recuperación, evidencia y comportamiento cuando falta contexto.</Typography>
                  </Box>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={agentPolicyForm.internetEnabled}
                        disabled={busy}
                        onChange={(event) => setAgentPolicyForm({
                          ...agentPolicyForm,
                          internetEnabled: event.target.checked,
                        })}
                      />
                    )}
                    label="Permitir que la IA busque información en internet"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                    Bloqueado por defecto. Al habilitarlo, la IA podrá solicitar búsqueda web al gateway configurado; la información externa se distinguirá de los documentos internos.
                  </Typography>
              <TextField
                select
                label="Prioridad de fuentes"
                value={agentPolicyForm.sourcePriority}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, sourcePriority: event.target.value as AgentPolicyForm['sourcePriority'] })}
                helperText="Orden que usará el agente para valorar el conocimiento recuperado."
                fullWidth
              >
                <MenuItem value="PUBLISHED_FIRST">Publicados y aprobados primero</MenuItem>
                <MenuItem value="MOST_RECENT">Documentación más reciente primero</MenuItem>
                <MenuItem value="COMPANY_FIRST">Contenido específico de la compañía primero</MenuItem>
                <MenuItem value="TEST_CORPUS">Corpus temporal de pruebas cuando esté habilitado</MenuItem>
              </TextField>
              <Box sx={{ px: 1 }}>
                <Typography variant="subtitle2">Confianza mínima requerida: {agentPolicyForm.minConfidence}%</Typography>
                <Slider
                  value={agentPolicyForm.minConfidence}
                  min={0}
                  max={100}
                  step={5}
                  marks={[{ value: 0, label: '0%' }, { value: 60, label: '60%' }, { value: 100, label: '100%' }]}
                  valueLabelDisplay="auto"
                  disabled={busy}
                  onChange={(_event, value) => setAgentPolicyForm({ ...agentPolicyForm, minConfidence: value as number })}
                  aria-label="Confianza mínima requerida"
                />
              </Box>
              <TextField
                select
                label="Cuando la consulta sea ambigua o falte información"
                value={agentPolicyForm.clarificationMode}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, clarificationMode: event.target.value as AgentPolicyForm['clarificationMode'] })}
                fullWidth
              >
                <MenuItem value="ASK">Pedir una aclaración antes de concluir</MenuItem>
                <MenuItem value="LIMITED_ANSWER">Dar una respuesta limitada e indicar la incertidumbre</MenuItem>
              </TextField>
              <TextField
                label="Respuesta cuando no entiende la consulta"
                value={agentPolicyForm.unrecognizedResponse}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, unrecognizedResponse: event.target.value })}
                helperText="Se mostrará al pedir aclaración y guiará al agente ante una consulta ambigua."
                inputProps={{ maxLength: 1000 }}
                minRows={2}
                multiline
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControlLabel
                  control={<Switch checked={agentPolicyForm.citationsRequired} disabled={busy} onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, citationsRequired: event.target.checked })} />}
                  label="Mostrar referencias documentales"
                />
                <FormControlLabel
                  control={<Switch checked={agentPolicyForm.requireEvidence} disabled={busy} onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, requireEvidence: event.target.checked })} />}
                  label="Exigir evidencia antes de afirmar"
                />
              </Stack>
              <TextField
                label="Reglas adicionales del agente"
                value={agentPolicyForm.rules}
                disabled={busy}
                onChange={(event) => setAgentPolicyForm({ ...agentPolicyForm, rules: event.target.value })}
                helperText="Reglas de calidad específicas. No incluyas secretos ni órdenes para ignorar las fuentes recuperadas."
                inputProps={{ maxLength: 4000 }}
                minRows={4}
                multiline
                fullWidth
              />
                </Stack>
              )}
              <Divider />
              <PrimaryButton
                disabled={busy}
                onClick={() => run(async () => {
                  const updated = await api.updateAgentPolicy({
                    rag_response_audience: agentPolicyForm.audience,
                    rag_response_style: agentPolicyForm.style,
                    rag_response_instructions: agentPolicyForm.instructions,
                    rag_agent_role: agentPolicyForm.role,
                    rag_response_format: agentPolicyForm.responseFormat,
                    rag_detail_level: agentPolicyForm.detailLevel,
                    rag_source_priority: agentPolicyForm.sourcePriority,
                    rag_min_confidence: agentPolicyForm.minConfidence,
                    rag_clarification_mode: agentPolicyForm.clarificationMode,
                    rag_citations_required: agentPolicyForm.citationsRequired,
                    rag_require_evidence: agentPolicyForm.requireEvidence,
                    rag_agent_rules: agentPolicyForm.rules,
                    rag_internet_enabled: agentPolicyForm.internetEnabled,
                    rag_company_data_enabled: agentPolicyForm.companyDataEnabled,
                    rag_streaming_enabled: agentPolicyForm.streamingEnabled,
                    rag_max_tokens: agentPolicyForm.maxTokens,
                    rag_courtesy_enabled: agentPolicyForm.courtesyEnabled,
                    rag_greeting_formality: agentPolicyForm.greetingFormality,
                    rag_farewell_formality: agentPolicyForm.farewellFormality,
                    rag_greeting_intent: agentPolicyForm.greetingIntent,
                    rag_greeting_response: agentPolicyForm.greetingResponse,
                    rag_thanks_response: agentPolicyForm.thanksResponse,
                    rag_farewell_response: agentPolicyForm.farewellResponse,
                    rag_safety_response: agentPolicyForm.safetyResponse,
                    rag_unrecognized_response: agentPolicyForm.unrecognizedResponse,
                    rag_blocked_terms: agentPolicyForm.blockedTerms,
                  })
                  setConfiguration(updated)
                  setRagForm(ragFormFrom(updated))
                  setAgentPolicyForm(agentPolicyFormFrom(updated))
                  setNotice('Política del agente de IA guardada.')
                })}
              >
                Guardar política del agente
              </PrimaryButton>
            </Stack>
          </Paper>
        )}
        {activeTab === 'permissions' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Administración de permisos</Typography>
              <Typography>
                Editor y Publicador son independientes. Conceder permisos de empresa exige pertenencia del usuario a esa
                empresa.
              </Typography>
              <TextField
                label="ID del usuario"
                type="number"
                value={grant.user_id || ''}
                onChange={(e) => setGrant({ ...grant, user_id: Number(e.target.value) })}
              />
              {scopeSelect(grant, (s) => setGrant({ ...grant, ...s }))}
              {(['can_edit', 'can_publish', 'active'] as const).map((k) => (
                <FormControlLabel
                  key={k}
                  control={
                    <Checkbox checked={grant[k]} onChange={(e) => setGrant({ ...grant, [k]: e.target.checked })} />
                  }
                  label={{ can_edit: 'Editor', can_publish: 'Publicador', active: 'Activo' }[k]}
                />
              ))}
              <Stack direction="row">
                {(Object.keys(audienceNames) as Audience[]).map((a) => (
                  <FormControlLabel
                    key={a}
                    control={
                      <Checkbox
                        checked={grant.readable_audiences.includes(a)}
                        onChange={(e) =>
                          setGrant({
                            ...grant,
                            readable_audiences: e.target.checked
                              ? [...grant.readable_audiences, a]
                              : grant.readable_audiences.filter((x) => x !== a),
                          })
                        }
                      />
                    }
                    label={audienceNames[a]}
                  />
                ))}
              </Stack>
              <Button
                disabled={busy || !grant.user_id || !grant.readable_audiences.length}
                onClick={() =>
                  run(async () => {
                    await api.grant(grant)
                    setPermissions(await api.permissions())
                    setAccess(await api.access())
                    setNotice('Permisos actualizados.')
                  })
                }
              >
                Guardar permiso
              </Button>
              {permissions.map((p) => (
                <Button
                  key={p.id}
                  onClick={() =>
                    setGrant({
                      user_id: p.user_id,
                      scope: p.scope,
                      company_id: p.company_id,
                      can_edit: p.can_edit,
                      can_publish: p.can_publish,
                      readable_audiences: p.readable_audiences,
                      active: p.active,
                    })
                  }
                >
                  Usuario #{p.user_id} · {scopeLabel(p)} · {p.active ? 'Activo' : 'Revocado'} · Editor{' '}
                  {p.can_edit ? 'sí' : 'no'} / Publicador {p.can_publish ? 'sí' : 'no'}
                </Button>
              ))}
            </Stack>
          </Paper>
        )}
        {showWorkspace && (
          <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Toda la documentación se convierte automáticamente a Markdown. El original permanece privado y el RAG de la IA local usa exclusivamente esa representación Markdown.
            </Alert>
            {pendingUpload && (
              <Alert
                severity={pendingUpload.phase === 'failed' ? 'error' : 'info'}
                action={
                  pendingUpload.phase === 'failed' ? (
                    <Button color="inherit" size="small" onClick={retryPendingUpload}>
                      Reintentar carga
                    </Button>
                  ) : undefined
                }
              >
                <Stack spacing={0.75} sx={{ minWidth: { sm: 420 } }}>
                  <Typography variant="body2" fontWeight={700}>
                    {pendingUpload.file.name}
                  </Typography>
                  <Typography variant="caption">
                    {resourceTypeLabel(pendingUpload.file.type)} · {formatBytes(pendingUpload.file.size)} · {pendingUpload.message}
                  </Typography>
                  {pendingUpload.phase !== 'failed' && (
                    <LinearProgress variant="determinate" value={pendingUpload.progress} />
                  )}
                </Stack>
              </Alert>
            )}
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
              {access.scopes.some((s) => s.edit) && (
                <>
                  <PrimaryButton
                    disabled={busy || dirty || Boolean(pendingUpload)}
                    onClick={() => {
                      const s = access.scopes.find((candidate) => candidate.edit)
                      if (!s) return
                      setScope({ scope: s.scope, company_id: s.company_id })
                      setDoc(null)
                      setVersionId(null)
                      setForm(emptyContent())
                      setCreating(true)
                      setDirty(false)
                    }}
                  >
                    Crear documento
                  </PrimaryButton>
                  <input
                    ref={directUploadInputRef}
                    type="file"
                    hidden
                    accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,text/plain,text/markdown,.docx,.md"
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      if (file) uploadNewDocument(file)
                    }}
                  />
                  <PrimaryButton
                    disabled={busy || dirty || Boolean(pendingUpload)}
                    onClick={() => directUploadInputRef.current?.click()}
                  >
                    Subir documento
                  </PrimaryButton>
                </>
              )}
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{ alignItems: { md: 'center' }, flexWrap: { md: 'nowrap' } }}
            >
              <TextField
                size="small"
                label="Buscar"
                value={filter.search || ''}
                sx={{ width: { xs: '100%', md: 250 } }}
                onChange={(e) => {
                  setPage(1)
                  setFilter({ ...filter, search: e.target.value })
                }}
              />
              <TextField
                select
                size="small"
                label="Estado"
                value={filter.state || ''}
                sx={{ width: { xs: '100%', md: 145 } }}
                onChange={(e) => {
                  setPage(1)
                  setFilter({ ...filter, state: e.target.value })
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(states).map(([key, text]) => (
                  <MenuItem key={key} value={key}>
                    {text}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Alcance"
                value={filter.scope || ''}
                sx={{ width: { xs: '100%', md: 135 } }}
                onChange={(e) => {
                  setPage(1)
                  setFilter({ ...filter, scope: e.target.value })
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="GLOBAL">GLOBAL</MenuItem>
                <MenuItem value="COMPANY">COMPANY</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Audiencia"
                value={filter.audience || ''}
                sx={{ width: { xs: '100%', md: 165 } }}
                onChange={(e) => {
                  setPage(1)
                  setFilter({ ...filter, audience: e.target.value })
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(audienceNames).map(([key, text]) => (
                  <MenuItem key={key} value={key}>
                    {text}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Categoría"
                value={filter.category || ''}
                sx={{ width: { xs: '100%', md: 210 } }}
                onChange={(e) => {
                  setPage(1)
                  setFilter({ ...filter, category: e.target.value })
                }}
              />
            </Stack>
            {items.length === 0 && <Typography>No hay documentos visibles para estos filtros.</Typography>}
            {items.map((item) => (
              <Button
                key={item.version_id}
                disabled={busy || dirty}
                aria-current={doc?.id === item.id && versionId === item.version_id ? 'page' : undefined}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: doc?.id === item.id && versionId === item.version_id ? 'action.selected' : 'transparent',
                  fontWeight: doc?.id === item.id && versionId === item.version_id ? 700 : 500,
                  justifyContent: 'flex-start',
                  px: 1,
                  textTransform: 'none',
                }}
                onClick={() => run(() => load(item.id, item.version_id))}
              >
                {item.title} · v{item.number} · {states[item.state]} · {scopeLabel(item)}
              </Button>
            ))}
            <Stack direction="row">
              <Button disabled={page === 1 || busy} onClick={() => setPage(page - 1)}>
                Anterior
              </Button>
              <Typography sx={{ p: 1 }}>Página {page}</Typography>
              <Button disabled={!more || busy} onClick={() => setPage(page + 1)}>
                Siguiente
              </Button>
            </Stack>
          </Stack>
          </Paper>
        )}
        {showWorkspace && (creating || doc) && (
          <Paper ref={editorRef} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h5">
                {creating ? 'Nuevo borrador' : `${v?.title} · ${states[v?.state || '']} · revisión ${doc?.revision}`}
              </Typography>
              {doc && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    select
                    label="Versión e historial"
                    value={versionId || ''}
                    sx={{ minWidth: 230 }}
                    disabled={busy || dirty}
                    onChange={(e) => {
                      const selected = doc.versions.find((x) => x.id === Number(e.target.value))
                      if (!selected) return
                      setVersionId(selected.id)
                      setForm(contentOf(selected))
                      setReason('')
                    }}
                  >
                    {doc.versions.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        v{x.number} · {states[x.state]}
                        {doc.current_version_id === x.id ? ' · vigente' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button disabled={busy} onClick={() => run(() => load(doc.id, versionId || undefined))}>
                    {dirty ? 'Descartar edición y recargar' : 'Recargar'}
                  </Button>
                </Stack>
              )}
              {creating ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ maxWidth: 940 }}>
                  <Box sx={{ width: { xs: '100%', sm: 400 } }}>{scopeSelect(selectedScope, setScope, true)}</Box>
                  {titleInput}
                </Stack>
              ) : (
                <Typography>
                  Alcance: {doc ? scopeLabel(doc) : ''} · ID #{doc?.id}
                </Typography>
              )}
              {doc &&
                v?.state === 'DRAFT' &&
                doc.capabilities.edit &&
                !doc.versions.some((x) => x.published_at) &&
                !v.resources.length && (
                  <Stack spacing={1}>
                    {scopeSelect(selectedScope, setScope, true)}
                    <Button
                      disabled={busy || dirty || scopeKey(selectedScope) === scopeKey(doc)}
                      onClick={() =>
                        run(async () => {
                          await api.scope(doc.id, doc.revision, selectedScope)
                          await load(doc.id, v.id)
                          await list()
                        })
                      }
                    >
                      Cambiar alcance del borrador
                    </Button>
                  </Stack>
                )}
              {!creating && titleInput}
              <TextField
                size="small"
                label="Resumen"
                multiline
                minRows={1}
                value={form.summary}
                onChange={(e) => change('summary', e.target.value)}
                disabled={!writable || busy}
                inputProps={{ maxLength: 2000 }}
                sx={{
                  maxWidth: { md: 640 },
                  '& .MuiOutlinedInput-root': { minHeight: 40 },
                  '& .MuiInputBase-inputMultiline': { py: 0.75 },
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ maxWidth: 520 }}>
                <TextField
                  size="small"
                  label="Categoría"
                  value={form.category}
                  onChange={(e) => change('category', e.target.value)}
                  disabled={!writable || busy}
                  inputProps={{ maxLength: 100 }}
                  fullWidth
                />
                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={form.type_code}
                  sx={{ minWidth: 180, maxWidth: 240 }}
                  onChange={(e) => change('type_code', e.target.value)}
                  disabled={!writable || busy}
                >
                  {access.types.map((t) => (
                    <MenuItem key={t.code} value={t.code}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Typography variant="body2">
                Audiencia: GLOBAL no significa público. El contenido interno requiere permisos explícitos.
              </Typography>
              <Stack direction="row" flexWrap="wrap">
                {(Object.keys(audienceNames) as Audience[]).map((a) => (
                  <FormControlLabel
                    key={a}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.audiences.includes(a)}
                        disabled={!writable || busy || !currentCaps?.audiences.includes(a)}
                        onChange={(e) =>
                          change(
                            'audiences',
                            e.target.checked ? [...form.audiences, a] : form.audiences.filter((x) => x !== a)
                          )
                        }
                      />
                    }
                    label={audienceNames[a]}
                  />
                ))}
              </Stack>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1}
                sx={{ maxWidth: 1080, alignItems: { lg: 'center' } }}
              >
                <TextField
                  select
                  size="small"
                  label="Modo de respuesta futuro"
                  value={form.response_mode}
                  sx={{ width: { xs: '100%', lg: 220 } }}
                  disabled={!writable || busy}
                  onChange={(e) => change('response_mode', e.target.value as Content['response_mode'])}
                >
                  <MenuItem value="FLEXIBLE">Flexible</MenuItem>
                  <MenuItem value="STRICT">Estricto</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Procedencia"
                  value={form.provenance}
                  sx={{ width: { xs: '100%', lg: 180 } }}
                  disabled={!writable || busy}
                  onChange={(e) => change('provenance', e.target.value as Content['provenance'])}
                >
                  <MenuItem value="MANUAL">Manual</MenuItem>
                  <MenuItem value="AI_ASSISTED">Asistido por IA</MenuItem>
                </TextField>
                {(['effective_from', 'review_after'] as const).map((k) => (
                  <TextField
                    key={k}
                    size="small"
                    label={k === 'effective_from' ? 'Vigencia desde (UTC)' : 'Revisar después (UTC)'}
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={form[k]?.slice(0, 16) || ''}
                    disabled={!writable || busy}
                    sx={{ width: { xs: '100%', lg: 245 } }}
                    onChange={(e) => change(k, e.target.value ? `${e.target.value}:00.000Z` : null)}
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                label="Contenido (Markdown)"
                multiline
                minRows={4}
                value={form.content}
                onChange={(e) => change('content', e.target.value)}
                disabled={!writable || busy}
                inputProps={{ maxLength: 200000 }}
                sx={{
                  maxWidth: 760,
                  '& .MuiInputBase-inputMultiline': { py: 0.75 },
                }}
              />
              {v && (
                <Alert severity={v.approved_for_ai ? 'success' : 'info'}>
                  IA: {v.approved_for_ai ? 'Aprobada' : 'No aprobada'} · Elegibilidad actual:{' '}
                  {v.ai_eligible ? 'Sí' : 'No'}
                  {v.approved_by && (
                    <>
                      {' '}
                      · Aprobador #{v.approved_by} · {v.approved_at}
                    </>
                  )}
                </Alert>
              )}
              {v?.state === 'PUBLISHED' && doc?.current_version_id === v.id && doc.capabilities.publish && (
                <Button
                  disabled={busy || dirty}
                  color={v.approved_for_ai ? 'warning' : 'primary'}
                  onClick={() =>
                    run(async () => {
                      const decision = await api.aiApproval(doc.id, v.id, doc.revision, !v.approved_for_ai)
                      setDoc((previous) =>
                        previous && previous.id === decision.document_id
                          ? {
                              ...previous,
                              revision: decision.revision,
                              versions: previous.versions.map((row) =>
                                row.id === decision.version_id
                                  ? {
                                      ...row,
                                      approved_for_ai: decision.approved_for_ai,
                                      approved_by: decision.approved_by,
                                      approved_at: decision.approved_at,
                                      ai_eligible: decision.ai_eligible,
                                    }
                                  : row
                              ),
                            }
                          : previous
                      )
                      setShowAudit(false)
                      setNotice(
                        v.approved_for_ai ? 'Aprobación IA revocada; artículo vigente.' : 'Aprobación IA registrada.'
                      )
                    })
                  }
                >
                  {v.approved_for_ai ? 'Revocar aprobación IA' : 'Aprobar para IA'}
                </Button>
              )}
              {form.content.trim() && (
                <>
                  <Typography variant="h6">Vista previa</Typography>
                  <Box sx={{ maxWidth: 760, overflowWrap: 'anywhere' }}>
                    <ReactMarkdown
                      skipHtml
                      components={{
                        img: () => <span>[Imagen externa omitida; usa recursos adjuntos]</span>,
                        a: ({ children, href }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {form.content}
                    </ReactMarkdown>
                  </Box>
                </>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {writable && (
                  <PrimaryButton
                    disabled={busy || !form.title.trim() || !form.audiences.length}
                    onClick={save}
                  >
                    Guardar borrador
                  </PrimaryButton>
                )}
                {v?.state === 'DRAFT' && doc?.capabilities.edit && (
                  <>
                    <Button disabled={busy || dirty || !v.content.trim()} onClick={() => action('submit')}>
                      Enviar a revisión
                    </Button>
                    {!doc.current_version_id && (
                      <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={busy || dirty}
                        onClick={() => {
                          if (
                            !window.confirm(
                              '¿Eliminar este borrador? No podrá volver a editarse ni consultarse.',
                            )
                          )
                            return
                          void run(async () => {
                            await api.discardDraft(doc.id, v.id, doc.revision)
                            setDoc(null)
                            setVersionId(null)
                            setCreating(false)
                            setDirty(false)
                            setNotice('Borrador eliminado.')
                            await list(1)
                          })
                        }}
                      >
                        Eliminar borrador
                      </Button>
                    )}
                  </>
                )}
                {v?.state === 'IN_REVIEW' && doc?.capabilities.publish && (
                  <>
                    <Button
                      disabled={busy || v.reviews.slice(-1)[0]?.decision !== 'PENDING'}
                      onClick={() => action('approve')}
                    >
                      Aprobar revisión
                    </Button>
                    {v.reviews.slice(-1)[0]?.decision === 'APPROVED' && (
                      <PrimaryButton disabled={busy} onClick={() => action('publish')}>
                        Publicar versión aprobada
                      </PrimaryButton>
                    )}
                  </>
                )}
                {['PUBLISHED', 'RETIRED'].includes(v?.state || '') && doc?.capabilities.edit && (
                  <Button
                    disabled={busy || doc.versions.some((x) => ['DRAFT', 'IN_REVIEW'].includes(x.state))}
                    onClick={() => action('copy')}
                  >
                    Crear nueva versión de trabajo
                  </Button>
                )}
                {v?.state === 'PUBLISHED' && doc?.capabilities.publish && (
                  <Button color="warning" disabled={busy} onClick={() => action('retire')}>
                    Retirar publicación
                  </Button>
                )}
              </Stack>
              {v?.state === 'IN_REVIEW' && doc?.capabilities.publish && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Motivo de rechazo"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                  />
                  <Button color="warning" disabled={busy || !reason.trim()} onClick={() => action('reject')}>
                    Rechazar y devolver a borrador
                  </Button>
                </Stack>
              )}
              {v && (
                <>
                  <Typography variant="h6">Recursos privados</Typography>
                  {v.resources.length ? (
                    <Stack spacing={1}>
                      {v.resources.map((r) => (
                        <Paper key={r.id} variant="outlined" sx={{ p: 1.25 }}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ sm: 'center' }}
                            justifyContent="space-between"
                          >
                            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                              <DescriptionOutlinedIcon color="primary" />
                              <Box minWidth={0}>
                                <Typography noWrap title={r.filename} fontWeight={700}>
                                  {r.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {resourceTypeLabel(r.mime)} · {formatBytes(r.size)} · Privado
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                size="small"
                                color={r.markdown_status === 'READY' ? 'success' : r.markdown_status === 'FAILED' ? 'error' : 'warning'}
                                label={
                                  r.markdown_status === 'READY'
                                    ? 'Markdown listo'
                                    : r.markdown_status === 'FAILED'
                                      ? 'Conversión fallida'
                                      : 'Conversión pendiente'
                                }
                              />
                              <IconButton
                                title={`Descargar ${r.filename}`}
                                aria-label={`Descargar ${r.filename}`}
                                disabled={busy}
                                onClick={() =>
                                  run(async () => {
                                    const blob = await api.download(r.id)
                                    const url = URL.createObjectURL(blob)
                                    const link = document.createElement('a')
                                    link.href = url
                                    link.download = r.filename
                                    link.click()
                                    setTimeout(() => URL.revokeObjectURL(url), 1000)
                                  })
                                }
                              >
                                <DownloadOutlinedIcon />
                              </IconButton>
                              {writable && (
                                <IconButton
                                  title={`Retirar ${r.filename}`}
                                  aria-label={`Retirar ${r.filename}`}
                                  color="warning"
                                  disabled={busy || dirty || Boolean(pendingUpload)}
                                  onClick={() =>
                                    run(async () => {
                                      if (!doc) return
                                      await api.retireResource(doc.id, v.id, doc.revision, r.id)
                                      await load(doc.id, v.id)
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              )}
                            </Stack>
                          </Stack>
                          {r.markdown_status === 'FAILED' && r.markdown_error && (
                            <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.75 }}>
                              {r.markdown_error}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No hay archivos adjuntos en esta versión.
                    </Typography>
                  )}
                  {writable && (
                    <Button component="label" disabled={busy || dirty || Boolean(pendingUpload)}>
                      Adjuntar PDF, DOCX, PNG, JPEG, TXT o Markdown · máximo 10 MiB
                      <input
                        type="file"
                        hidden
                        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,text/plain,text/markdown,.docx,.md"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          e.target.value = ''
                          if (!file) return
                          if (file.size > 10485760) {
                            setError('El archivo supera 10 MiB.')
                            return
                          }
                          if (!doc) return
                          void attachFile(file, doc, v).catch((uploadError) => {
                            setPendingUpload((current) =>
                              current
                                ? { ...current, phase: 'failed', message: errorText(uploadError) }
                                : current,
                            )
                            setError(`No se pudo adjuntar el archivo. ${errorText(uploadError)}`)
                          })
                        }}
                      />
                    </Button>
                  )}
                  <Typography variant="caption">
                    Los archivos con texto se convierten al adjuntarlos. Los documentos escaneados quedan pendientes de extracción y no se envían al RAG hasta disponer de Markdown.
                  </Typography>
                  <Typography>
                    Autor #{v.created_by} · Creado {v.created_at} · Modificado {v.updated_at} · Publicación:{' '}
                    {v.published_at || 'Pendiente'} · Retiro: {v.retired_at || '—'} · Reemplazo:{' '}
                    {v.replaced_by_version_id || '—'}
                  </Typography>
                  {v.reviews.map((r) => (
                    <Alert key={r.id} severity={r.decision === 'REJECTED' ? 'warning' : 'info'}>
                      Revisión #{r.id}: {r.decision} · solicitante #{r.requested_by} · responsable #
                      {r.decided_by || 'pendiente'} · {r.reason}
                    </Alert>
                  ))}
                </>
              )}
              {doc && (doc.capabilities.edit || doc.capabilities.publish) && (
                <>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        setAuditPage(1)
                        const initialEvents = await api.audit(doc.id, 1)
                        setAudits(initialEvents)
                        setAuditAnchor(initialEvents[0]?.id || null)
                        setShowAudit(!showAudit)
                      })
                    }
                  >
                    Historial editorial y auditoría
                  </Button>
                  {showAudit && (
                    <Stack spacing={1}>
                      {audits.map((a) => (
                        <Box key={a.id}>
                          <Chip label={a.action} size="small" /> #{a.id} · actor #{a.actor_id} · rol {a.actor_role} ·
                          empresa {a.company_id || 'GLOBAL'}
                          {a.transversal_access ? ' · Super Admin transversal' : ''} · versión {a.version_id || '—'} ·{' '}
                          {a.created_at} · resultado {a.outcome}
                          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(a.changes)}
                          </Typography>
                        </Box>
                      ))}
                      <Button
                        disabled={busy || auditPage === 1}
                        onClick={() =>
                          run(async () => {
                            setAudits(await api.audit(doc.id, auditPage - 1, auditAnchor))
                            setAuditPage(auditPage - 1)
                          })
                        }
                      >
                        Auditoría anterior
                      </Button>
                      <Button
                        disabled={busy || audits.length < 100}
                        onClick={() =>
                          run(async () => {
                            setAudits(await api.audit(doc.id, auditPage + 1, auditAnchor))
                            setAuditPage(auditPage + 1)
                          })
                        }
                      >
                        Más auditoría
                      </Button>
                    </Stack>
                  )}
                </>
              )}
              {dirty && (
                <Alert severity="info">
                  Hay cambios sin guardar. Guarda o recarga antes de cambiar de documento o realizar una transición.
                </Alert>
              )}
            </Stack>
          </Paper>
        )}
        <ModalLoader isLoading={busy && !operationalChecking} />
      </Stack>
    </Box>
  )
}
export default function Knowledge(): JSX.Element {
  return (
    <MainLayout>
      <KnowledgePanel />
    </MainLayout>
  )
}
